import {
	addUnit,
	formatDate,
	isAfter,
	isBefore,
	isSame,
	parseDate,
	startOf,
} from "@sembark-travel/datetime-utils"
import * as Validator from "yup"
import type { IHotel } from "../Hotels"
import type { THotelOtaMealPlan, TOtaMealPlan } from "../HotelOtaMealPlans"
import type {
	THotelOtaRoomType,
	TOtaRoomType,
	TOtaRoomTypePaxTypeConfig,
} from "../HotelOtaRoomTypes"
import { isNumeric } from "@sembark-travel/number-utils"
import {
	TDay,
	TPricingType,
	TRateSheet,
	TRateSheetHead,
	TRateSheetPaxType,
	TRateSheetPolicy,
	TRateSheetSeason,
	TRateSheetSeasonInterval,
	TRoundingPointSchema,
} from "./store"
import {
	INewRateSheetFormSchema,
	IRateBlockSchema,
	ISeasonSchema,
	TGridRate,
	TSeasonFormInterval,
} from "./NewItem"
import {
	TCancellationPolicy,
	TCancellationPolicyDetail,
} from "../CancellationPolicy"
import { TPaymentPolicy, TPaymentPolicyDetail } from "../PaymentPolicy"
import type { TPolicyHeadSummary } from "../shared/policies"

// Key for one hotel x season rate block, shared by the live rates map, the
// baseline map and every summary keyed off a block.
export function rateKey(
	seasonIdentifier: string | number,
	hotelId: number | string
): string {
	return `${seasonIdentifier}-${hotelId}`
}

// Every rate block of a sheet, keyed by season x hotel. Written under both the
// season's uid and its index, because a caller holding one has no way back to
// the other — the wizard addresses seasons by uid so a reorder cannot move a
// block, while anything reading a saved sheet only has positions.
//
// `hotelIds` drops blocks for hotels the sheet no longer carries: a block left
// behind by a hotel removed in step 1 is not part of the sheet and must not be
// diffed against.
export function buildSheetRatesRef(
	seasons: ISeasonSchema[] | undefined,
	hotelIds?: Set<number>
): Map<string, IRateBlockSchema> {
	const byKey = new Map<string, IRateBlockSchema>()
	seasons?.forEach((season, seasonIdx) => {
		season?.rate_blocks?.forEach((block) => {
			const hotelId = Number(block?.hotel)
			if (!hotelId) return
			if (hotelIds && !hotelIds.has(hotelId)) return
			byKey.set(rateKey(season._uid || seasonIdx, hotelId), block)
			byKey.set(rateKey(seasonIdx, hotelId), block)
		})
	})
	return byKey
}

let seasonUidSeq = 0
export function createSeasonUid(): string {
	return `season_${++seasonUidSeq}`
}

export const ALL_DAYS: TDay[] = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
]

export function newInterval(): TSeasonFormInterval {
	return { from: null, to: null, days: [...ALL_DAYS] }
}

// export function numberToHexColor(value: number): string {
// 	const hash = (value * 2654435761) >>> 0
// 	const color = hash & 0xffffff

// 	return `#${color.toString(16).padStart(6, "0").toUpperCase()}`
// }

// Occupancy configuration of a room in a rate block
export type TRoomConfig = {
	ota_room_type: number
	base_adult_occupancy: number
	base_child_occupancy: number
	max_occupancy: number
	max_adult_occupancy: number
	max_child_occupancy: number
}

// A pax type priced by a rate block — one per column of its rate table
export type TPaxType = {
	occupancy: string
	pax_type: string
	pax_count: number | null
	start_age: number | null
	end_age: number | null
	derived_from_occupancy: string | null
	derivation_multiplier: number | null
	derivation_adder: number | null
}

export type TPayloadRate = {
	ota_room_type: number
	ota_meal_plan: number
	occupancy: string
	rate_before_tax: number | null
	rate_before_tax_unrounded: number | null
	is_manual: 1 | 0
}

// Priced either per breakfast, lunch or dinner (`meal_type`) or, for a custom
// meal plan the backend has no meal type for, per `ota_meal_plan` id — in which
// case `meal_type` is null.
export type TPayloadMealRate = {
	meal_type: string | null
	ota_meal_plan?: number | null
	per_adult_rate_before_tax: number | null
	per_child_rate_before_tax: number | null
}

export type TPayload = {
	name: string
	hotels: {
		hotel: number
		cancellation_policy: number
		payment_policy: number
	}[]
	seasons: {
		name: string
		intervals: Omit<TRateSheetSeasonInterval, "id">[]
		blocks: {
			hotel: number
			rounding_point: number | null
			pricing_type: TPricingType
			child_meal_rate_same_as_adult: 1 | 0
			is_lower_occupancy_rate_configured: 1 | 0
			cancellation_policy: number
			cancellation_policy_is_manual: 1 | 0
			payment_policy: number
			payment_policy_is_manual: 1 | 0
			meal_plans: {
				ota_meal_plan: number
				category: string
				is_base: 1 | 0
				includes_all_room_types: 1 | 0
				ota_room_types?: number[]
			}[]
			meal_rates: TPayloadMealRate[]
			rooms: TRoomConfig[]
			pax_types: TPaxType[]
			rates: TPayloadRate[]
		}[]
	}[]
}

export const roudingOptions: TRoundingPointSchema[] = [
	{ id: null, name: "None" },
	{ id: 1, name: "1" },
	{ id: 5, name: "5" },
	{ id: 10, name: "10" },
	{ id: 50, name: "50" },
	{ id: 100, name: "100" },
]

// Not user editable, a room's occupancy configuration is always derived from
// the hotel's OTA room types.
function toRoomConfig(roomType: THotelOtaRoomType): TRoomConfig {
	return {
		ota_room_type: roomType.id,
		base_adult_occupancy: roomType.base_adult_occupancy || 0,
		base_child_occupancy: roomType.base_child_occupancy || 0,
		max_occupancy: roomType.max_occupancy || 0,
		max_adult_occupancy: roomType.max_adult_occupancy || 0,
		max_child_occupancy: roomType.max_child_occupancy || 0,
	}
}

export function buildRooms(
	roomTypes: THotelOtaRoomType[] | undefined
): TRoomConfig[] {
	return (roomTypes || []).map(toRoomConfig)
}

export type TBaseOccupancyChange = { from: number; to: number }

// Rooms whose base adult occupancy on the hotel no longer matches the one the
// block was priced at, keyed by room type id. A room the block does not price
// is not a change.
export function baseOccupancyChanges(
	savedRooms: Pick<TRoomConfig, "ota_room_type" | "base_adult_occupancy">[],
	roomTypes: Pick<THotelOtaRoomType, "id" | "base_adult_occupancy">[]
): Map<number, TBaseOccupancyChange> {
	const changes = new Map<number, TBaseOccupancyChange>()
	savedRooms.forEach((room) => {
		const roomType = roomTypes.find(
			(rt) => Number(rt.id) === Number(room.ota_room_type)
		)
		if (!roomType) return
		if (roomType.base_adult_occupancy === room.base_adult_occupancy) return
		changes.set(Number(room.ota_room_type), {
			from: room.base_adult_occupancy,
			to: roomType.base_adult_occupancy,
		})
	})
	return changes
}

// A saved BASE price was priced for the occupancy the room had back then.
// Keeping it under a new occupancy lets the meal plan rows re-derive from it
// with the new pax count, so the base meal plan's BASE price is cleared for the
// user to price again. Everything derived from it goes blank with it.
export function resetChangedBaseOccupancyRates(
	seasons: ISeasonSchema[],
	hotels: IHotel[]
): ISeasonSchema[] {
	return seasons.map((season) => ({
		...season,
		rate_blocks: season.rate_blocks.map((block) => {
			const hotel = hotels.find((h) => Number(h.id) === Number(block.hotel))
			if (!hotel) return block
			const roomTypes = hotel.ota_room_types || []
			const changes = baseOccupancyChanges(block.rooms, roomTypes)
			if (!changes.size) return block
			const baseMealPlanId = block.meal_plans.find((mp) => mp.is_base)
				?.ota_meal_plan.id
			return {
				...block,
				rooms: block.rooms.map((room) => {
					const roomType = roomTypes.find(
						(rt) => Number(rt.id) === Number(room.ota_room_type)
					)
					return roomType && changes.has(Number(room.ota_room_type))
						? toRoomConfig(roomType)
						: room
				}),
				rates: block.rates.map((rate) =>
					changes.has(Number(rate.ota_room_type)) &&
					rate.occupancy === "base" &&
					Number(rate.ota_meal_plan) === Number(baseMealPlanId)
						? {
								...rate,
								rate_before_tax: null,
								rate_before_tax_unrounded: null,
							}
						: rate
				),
			}
		}),
	}))
}

// A pax type as the backend configures it — on the hotel itself
// (`pax_type_of_childs`) or on one of its OTA room types (`pax_types`,
// `pax_types_with_lower_occupancies_configuration`). `pax_count` arrives as a
// number for some pax types and as a string for others.
// A saved block also prices the lower occupancies, whose `pax_type` is outside
// the room's own `TPaxTypeCode` values (e.g. "A"), so it stays a string here.
type TBackendPaxType = Omit<
	TOtaRoomTypePaxTypeConfig,
	"pax_count" | "pax_type"
> & {
	pax_type: string
	pax_count: number | string | null
}

// The identity fields the column builders read off a saved block's pax types.
// Leaving the derivation fields out is what lets both the API shape (decimal
// strings) and the form shape (numbers) feed them.
type TPaxTypeColumnSource = Omit<
	TRateSheetPaxType,
	"id" | "derivation_multiplier" | "derivation_adder"
>

function paxCountOf(
	paxType: Pick<TBackendPaxType, "pax_count">
): number | null {
	if (paxType.pax_count == null) return null
	const count = Number(paxType.pax_count)
	return isNaN(count) ? null : count
}

// Age range a child pax type covers, read off the occupancy the backend named it
// with — "CNB (6-12)" prices children aged 6 to 12. The backend sends no
// separate age fields on the pax type, and the range is what the payload's
// `start_age`/`end_age` carry.
function agesFromOccupancy(occupancy: string): {
	startAge: number | null
	endAge: number | null
} {
	const matched = (occupancy || "").match(/\((\d+)\s*-\s*(\d+)\)/)
	if (!matched) return { startAge: null, endAge: null }
	return { startAge: Number(matched[1]), endAge: Number(matched[2]) }
}
export type TKnownPaxType = "AWEB" | "CNB" | "CWEB"

// A column of the room rates table, also describing the pax type it prices.
// `occupancy` and `paxCount` are the backend's own naming for the pax type, so
// the payload can quote them back instead of the grid re-deriving them.
export type TRoomCol = {
	key: string
	label: string
	paxType: TKnownPaxType | (string & {})
	startAge: number | null
	endAge: number | null
	occupancy: string
	paxCount: number | null
}

function toRoomCol(paxType: TBackendPaxType, withLabels: boolean): TRoomCol {
	const occupancy = (paxType.occupancy || "").trim()
	return {
		key: occupancy,
		// The occupancy doubles as the header — "CNB (6-12)" reads "CNB(6-12)"
		label: withLabels ? occupancy : "",
		paxType: (paxType.pax_type || "").trim().toUpperCase(),
		...agesFromOccupancy(occupancy),
		occupancy,
		paxCount: paxCountOf(paxType),
	}
}

// Builds columns for rate tables and payload data. e.g base, aweb, cnb(5y-12y), etc
//
// The columns are the hotel's own pax type configuration: the extra bed types
// its OTA room types carry — their union, since every room type names the same
// ones — then the child types on the hotel. BASE is a room's own occupancy,
// which the grid prices in its BASE column rather than an extra bed one.
export function buildRoomCols(hotel: IHotel, withLabels = true): TRoomCol[] {
	const extraBedByOccupancy = new Map<string, TBackendPaxType>()
	for (const roomType of hotel.ota_room_types || []) {
		for (const paxType of roomType.pax_types || []) {
			const occupancy = (paxType.occupancy || "").trim()
			if (!occupancy) continue
			if ((paxType.pax_type || "").toUpperCase() === "BASE") continue
			if (!extraBedByOccupancy.has(occupancy)) {
				extraBedByOccupancy.set(occupancy, paxType)
			}
		}
	}

	return [
		...extraBedByOccupancy.values(),
		...(hotel.pax_type_of_childs || []),
	].map((paxType) => toRoomCol(paxType, withLabels))
}

// Occupancy labels for a given adult count, used by the lower occupancy columns
const OCCUPANCY_INFO: Record<number, { key: string; label: string }> = {
	1: { key: "single", label: "Single" },
	2: { key: "double", label: "Double" },
	3: { key: "triple", label: "Triple" },
	4: { key: "quad", label: "Quad" },
	5: { key: "quint", label: "Quint" },
	6: { key: "6pax", label: "6 Adults" },
	7: { key: "7pax", label: "7 Adults" },
	8: { key: "8pax", label: "8 Adults" },
	9: { key: "9pax", label: "9 Adults" },
	10: { key: "10pax", label: "10 Adults" },
}

export function getOccupancyInfo(count: number): {
	key: string
	label: string
} {
	return (
		OCCUPANCY_INFO[count] || { key: `pax_${count}`, label: `${count} Adults` }
	)
}

// Adult count behind a lower occupancy, which the backend names "A2"
function getOccupancyCount(occupancy: string): number | null {
	const matched = occupancy.trim().match(/^A(\d+)$/i)
	return matched ? Number(matched[1]) : null
}

// The rate grid keeps a dense row x column array so cells can be addressed
// positionally. Cells a room type cannot take are marked `enabled: 0` and exist
// only to hold that shape, so they are dropped here — the single boundary
// between the grid and the rest of the form.
export function toBlockRates(
	gridRates: TGridRate[] | undefined
): IRateBlockSchema["rates"] {
	return (gridRates || [])
		.filter((rate) => rate && rate.enabled !== 0 && rate.enabled !== false)
		.map(({ enabled: _enabled, ...rate }) => rate)
}

type TGridRow = {
	rt: {
		id: number | string
		pax_types?: Pick<TBackendPaxType, "pax_type" | "pax_count">[]
	}
	mp: { id: number | string }
}

// Identity of one rate within a block — room type x meal plan x occupancy. The
// single key every grid is seeded through, so the live values and the baseline
// they are diffed against cannot drift apart. Keyed on the payload occupancy, so
// rates seeded straight from the API address the same cells as the grid's own.
export function gridRateKey(
	rate: Pick<TGridRate, "ota_room_type" | "ota_meal_plan" | "occupancy">
): string {
	const occupancy = toPaxOccupancy(rate.occupancy || "")
	return `${rate.ota_room_type}_${rate.ota_meal_plan}_${occupancy}`
}

// Builds the dense row x column rate array the grid addresses positionally.
// Callers supply the rates to seed values from — the live block values or the
// change-dot baseline. Building both here, off the same geometry and the same
// key, is what keeps them index-aligned, which the comparison depends on.
export function buildGridRates(
	visibleRows: TGridRow[],
	lowerCols: Pick<TLowerCol, "occ" | "key">[],
	roomCols: TRoomCol[],
	configureLowerOccupancy: boolean,
	sourceRates: IRateBlockSchema["rates"] | undefined
): TGridRate[] {
	const sourceByKey = new Map<string, IRateBlockSchema["rates"][number]>()
	for (const rate of sourceRates || []) {
		if (!rate) continue
		const key = gridRateKey(rate)
		// One cell should appear once, but the grid writes its rates positionally
		// and can hand up two entries for the same cell if it is ever laid out on a
		// column count its array was not built for. A blank duplicate must not be
		// allowed to displace the rate that was actually entered — priced beats
		// blank whichever order they arrive in.
		const seen = sourceByKey.get(key)
		if (seen && seen.rate_before_tax != null && rate.rate_before_tax == null) {
			continue
		}
		sourceByKey.set(key, rate)
	}

	const gridRates: TGridRate[] = []

	for (const row of visibleRows) {
		const roomTypeId = Number(row.rt.id)
		const mealPlanId = Number(row.mp.id)
		const baseOccupancy = getRoomBaseOccupancy(row.rt)

		function pushCell(colKey: string, placeholder = false) {
			const existing = placeholder
				? undefined
				: sourceByKey.get(
						gridRateKey({
							ota_room_type: roomTypeId,
							ota_meal_plan: mealPlanId,
							occupancy: colKey,
						})
					)
			gridRates.push({
				ota_room_type: roomTypeId,
				occupancy: colKey,
				ota_meal_plan: mealPlanId,
				rate_before_tax: existing?.rate_before_tax ?? null,
				// Carried through a remount for cells the derivation engine does not
				// rewrite — a column whose derivation is set to manual mode, say, still
				// holding the value it was saved with.
				rate_before_tax_unrounded: existing?.rate_before_tax_unrounded ?? null,
				enabled: placeholder ? 0 : 1,
				is_manual: existing?.is_manual ?? false,
			})
		}

		if (configureLowerOccupancy) {
			for (const col of lowerCols) {
				// A room cannot be sold below its own base occupancy, so this cell is
				// a placeholder that only holds the grid's shape
				pushCell(col.key, col.occ >= baseOccupancy)
			}
		}
		pushCell("base")
		for (const col of roomCols) {
			pushCell(col.key)
		}
	}

	return gridRates
}

export function getRoomBaseOccupancy(roomType: {
	pax_types?: Pick<TBackendPaxType, "pax_type" | "pax_count">[]
	base_adult_occupancy?: number | string
}): number {
	const basePaxType = (roomType.pax_types || []).find(
		(paxType) => (paxType.pax_type || "").toUpperCase() === "BASE"
	)
	const occupancy = basePaxType ? paxCountOf(basePaxType) : null
	if (occupancy !== null && occupancy > 0) return occupancy
	if (roomType.base_adult_occupancy != null) {
		const num = Number(roomType.base_adult_occupancy)
		if (!Number.isNaN(num) && num > 0) return num
	}
	return 2
}

export type THotelRateErrors = Partial<Record<TIssueSource, string>>

export const MAX_RATE = 999999
export const rateExceedsMaxMessage = `Input value cannot exceed ${MAX_RATE}.`

const validateRate = (
	value: number | null | undefined,
	invalidMessage: string
): string | null => {
	if (!isNumeric(value) || value < 0) {
		return invalidMessage
	}

	if (value > MAX_RATE) {
		return rateExceedsMaxMessage
	}

	return null
}

/**
 * Performs ceiling rounding on a number according to the given rounding point.
 * For a rounding point P (e.g. 1, 5, 10, 50, 100), rounds the value up to the nearest multiple of P:
 *   ceil(value / P) * P
 * If roundingPoint is null/undefined/<=0 (e.g. "None"), returns the unrounded value.
 */
export function applyCeilingRounding(
	value: number | null | undefined,
	roundingPoint: number | null | undefined
): number | null {
	if (value === null || value === undefined || isNaN(value)) return null
	const num = Number(value)
	if (isNaN(num)) return null

	if (!hasRoundingPoint(roundingPoint)) {
		// When no rounding point (or "None") is selected, return unrounded raw value
		return Number(num.toFixed(4))
	}
	const point = Number(roundingPoint)

	const quotient = Number((num / point).toFixed(8))
	return Math.ceil(quotient) * point
}

// Whether a block actually rounds. "None" arrives as a null id, and anything
// non-positive or unparseable is treated the same way — there is nothing to
// round to. The single reading of the rounding point, so `applyCeilingRounding`
// and `unroundedRateFor` cannot disagree about when rounding happened.
function hasRoundingPoint(roundingPoint: number | null | undefined): boolean {
	if (roundingPoint == null) return false
	const point = Number(roundingPoint)
	return !isNaN(point) && point > 0
}

/**
 * The value a rate held before `applyCeilingRounding` was applied to it, as the
 * `rate_before_tax_unrounded` field carries it.
 *
 * Null unless rounding actually happened: with no rounding point the rate *is*
 * the unrounded value and a second copy of it would only be able to drift.
 * Callers pass derived values only — a manual rate is the user's own number and
 * never had an unrounded form, so its field stays null.
 *
 * Normalised to 4 decimals, matching `applyCeilingRounding`'s own no-rounding
 * path. Chained derivations multiply their way into float artifacts —
 * `(1000 * 0.33) * 1.1` is `363.00000000000006` — and this is what keeps the
 * stored value one the backend can reproduce.
 */
export function unroundedRateFor(
	value: number | null | undefined,
	roundingPoint: number | null | undefined
): number | null {
	if (!hasRoundingPoint(roundingPoint)) return null
	if (value === null || value === undefined) return null
	const num = Number(value)
	if (isNaN(num)) return null
	return Number(num.toFixed(4))
}

// Same 4-decimal normalisation, applied while a derivation chain is still being
// walked — where there is no rounding point to gate on, because the chain has to
// stay exact whether or not its result will be rounded.
export function normalizeDerivedRate(
	value: number | null | undefined
): number | null {
	if (value === null || value === undefined) return null
	const num = Number(value)
	if (isNaN(num)) return null
	return Number(num.toFixed(4))
}

// A rate the grid fills in on its own: a column derived from another column, or
// any row of a meal plan other than the base one, which is priced off the base
// plan's rate plus the meal add on. There is no input to fill for these, so a
// blank one is not a missing entry — it means the cell it derives from is blank,
// and that cell reports the error itself. Overriding a derived cell by hand
// makes it the user's value again, and so validated again.
export function isDerivedRate(
	rate: Pick<TGridRate, "occupancy" | "ota_meal_plan" | "is_manual">,
	{
		derivedOccupancies,
		baseMealPlanId,
	}: { derivedOccupancies: Set<string>; baseMealPlanId: number | null }
): boolean {
	if (rate.is_manual) return false
	if (
		baseMealPlanId !== null &&
		Number(rate.ota_meal_plan) !== baseMealPlanId
	) {
		return true
	}
	return derivedOccupancies.has(toPaxOccupancy(rate.occupancy || ""))
}

// The occupancies a block derives, as stored in its pax types. A block that has
// never been opened carries none, which only costs it the exemption above.
function derivedOccupanciesFromPaxTypes(
	paxTypes: IRateBlockSchema["pax_types"] | undefined
): Set<string> {
	const occupancies = new Set<string>()
	paxTypes?.forEach((paxType) => {
		if (paxType?.derived_from_occupancy) {
			occupancies.add(toPaxOccupancy(paxType.occupancy))
		}
	})
	return occupancies
}

// Same set, from the grid's live column dropdowns. BASE and AWEB are always
// typed in, matching how the grid renders them.
export function derivedOccupanciesFromColumns(
	columnDerivations: Record<string, TColumnDerivation> | undefined
): Set<string> {
	const occupancies = new Set<string>()
	Object.entries(columnDerivations || {}).forEach(([colKey, derivation]) => {
		const key = colKey.toLowerCase()
		if (key === "base" || key === "aweb") return
		if (derivation?.mode === "derived") {
			occupancies.add(toPaxOccupancy(colKey))
		}
	})
	return occupancies
}

// The only parts of a block the error checks read — kept narrow so a form that
// carries no policies (Hotels calendar rates) can be validated too.
type TRateBlockPricing = Pick<
	IRateBlockSchema,
	| "rates"
	| "pax_types"
	| "meal_plans"
	| "meal_rates"
	| "child_meal_rate_same_as_adult"
>

// Validate a single hotel's rate block. Shared by the whole-sheet check below
// and by the per-flush check, so the two cannot drift apart. Narrows the full
// message lists from `computeBlockErrorMessages` down to the first failure per
// source, which is what the submit gate and the inline banners render.
export function computeBlockErrors(
	hotelRate: TRateBlockPricing | undefined,
	hotel: IHotel
): THotelRateErrors {
	const hotelErrors: THotelRateErrors = {}
	const { roomRates, mealRates } = computeBlockErrorMessages(hotelRate, hotel)

	if (roomRates.length > 0) {
		hotelErrors.roomRates = roomRates[0]
	}
	if (mealRates.length > 0) {
		hotelErrors.mealRates = mealRates[0]
	}

	return hotelErrors
}

export type TColumnDerivation = {
	mode: "manual" | "derived"
	sourceColKey?: string
	multiplier?: number
	constant?: number
}

const NO_DERIVATION = {
	derived_from_occupancy: null,
	derivation_multiplier: null,
	derivation_adder: null,
}

function toDerivation(derivation: TColumnDerivation | undefined) {
	if (!derivation || derivation.mode !== "derived") {
		return NO_DERIVATION
	}
	return {
		derived_from_occupancy: derivation.sourceColKey
			? toPaxOccupancy(derivation.sourceColKey)
			: null,
		derivation_multiplier: derivation.multiplier ?? 1,
		derivation_adder: derivation.constant ?? 0,
	}
}

// Inverse of `toDerivation`: the derivation a column was saved with, read off
// its pax type. A reset button in edit mode restores this — the configuration
// the sheet was loaded with, before the user touched the column.
function fromPaxTypeDerivation(
	paxType:
		| Pick<
				TPaxType,
				"derived_from_occupancy" | "derivation_multiplier" | "derivation_adder"
		  >
		| undefined
): TColumnDerivation {
	if (!paxType?.derived_from_occupancy) return { mode: "manual" }
	return {
		mode: "derived",
		sourceColKey: fromPaxOccupancy(paxType.derived_from_occupancy),
		multiplier: paxType.derivation_multiplier ?? 1,
		constant: paxType.derivation_adder ?? 0,
	}
}

// The derivation each extra pax column was saved with, keyed the same way the
// live `columnDerivations` state is. A block with no saved pax types (the
// create flow, or a block never opened) has nothing to reset to.
// The column keys a pax type list covers. A pax type without an occupancy names
// no column, so it cannot be matched against one.
export function occupancyKeysOf(
	paxTypes: IRateBlockSchema["pax_types"] | undefined
): Set<string> {
	return new Set(
		(paxTypes || [])
			.map((pt) => pt?.occupancy)
			.filter((occ): occ is string => Boolean(occ))
	)
}

export function columnDerivationsFromPaxTypes(
	paxTypes: IRateBlockSchema["pax_types"] | undefined,
	roomCols: Pick<TRoomCol, "key">[]
): Record<string, TColumnDerivation> {
	const byOccupancy = new Map<string, IRateBlockSchema["pax_types"][number]>()
	paxTypes?.forEach((pt) => {
		if (pt?.occupancy) byOccupancy.set(pt.occupancy, pt)
	})

	const result: Record<string, TColumnDerivation> = {}
	roomCols.forEach((col) => {
		result[col.key] = fromPaxTypeDerivation(
			byOccupancy.get(toPaxOccupancy(col.key))
		)
	})
	return result
}

// Whether a column's live derivation still matches the one it was saved with —
// what a reset button in edit mode is offered against.
export function columnDerivationChanged(
	current: TColumnDerivation | undefined,
	baseline: TColumnDerivation | undefined
): boolean {
	const curr = current ?? { mode: "manual" as const }
	const base = baseline ?? { mode: "manual" as const }
	if (curr.mode !== base.mode) return true
	if (curr.mode === "manual") return false
	return (
		curr.sourceColKey !== base.sourceColKey ||
		(curr.multiplier ?? 1) !== (base.multiplier ?? 1) ||
		(curr.constant ?? 0) !== (base.constant ?? 0)
	)
}

// The pax types priced by a rate block, one per column of the rate table:
// lower occupancies, then BASE, then AWEB/CNB/CWEB.
// Callers pass an empty `lowerCols` when the block does not configure lower
// occupancy rates, since those columns are then absent from the table.
//
// Every column carries the backend's own naming for the pax type it prices, so
// this quotes that back rather than re-deriving it.
export function buildPaxTypes(
	lowerCols: Pick<TLowerCol, "occ" | "occupancy" | "paxType">[],
	roomCols: TRoomCol[],
	columnDerivations: Record<string, TColumnDerivation>
): TPaxType[] {
	return [
		...lowerCols.map((col) => ({
			occupancy: col.occupancy,
			pax_type: col.paxType,
			pax_count: col.occ,
			start_age: null,
			end_age: null,
			...NO_DERIVATION,
		})),
		{
			occupancy: "BASE",
			pax_type: "BASE",
			// BASE has no count of its own to carry: it is whatever each room type
			// is sold at, which the block already states per room in
			// `rooms[].base_adult_occupancy`. One number across the block could only
			// ever be right for one of them.
			pax_count: null,
			start_age: null,
			end_age: null,
			...NO_DERIVATION,
		},
		...roomCols.map((col) => ({
			occupancy: col.occupancy,
			pax_type: col.paxType,
			pax_count: col.paxCount,
			start_age: col.startAge,
			end_age: col.endAge,
			...toDerivation(columnDerivations[col.key]),
		})),
	]
}

// Pax counts come from the columns, which carry the backend's own `pax_count`
// for the pax type they price.
export function getPaxCountForColOffset(
	colIdx: number,
	rtBaseOcc: number,
	lowerCols: Pick<TLowerCol, "occ">[],
	roomCols: Pick<TRoomCol, "paxCount">[],
	configureLowerOccupancy: boolean
): number {
	const numLowerCols = configureLowerOccupancy ? lowerCols.length : 0
	if (configureLowerOccupancy && colIdx < numLowerCols) {
		return lowerCols[colIdx].occ
	}
	const baseColIdx = numLowerCols
	if (colIdx === baseColIdx) {
		return rtBaseOcc
	}
	return roomCols[colIdx - baseColIdx - 1]?.paxCount ?? 1
}

export function calculateMealPlanAddon(
	mealPlan: TOtaMealPlan,
	mealRates: {
		meal_type?: string | null
		ota_meal_plan?: number | null
		per_adult_rate_before_tax?: number | string | null
		per_child_rate_before_tax?: number | string | null
	}[],
	useChildRate = false
): { isCalculable: boolean; addonCost: number | null } {
	const rateOf = (item: (typeof mealRates)[number] | undefined) => {
		if (item) {
			const val = useChildRate
				? item.per_child_rate_before_tax
				: item.per_adult_rate_before_tax
			if (isNumeric(val)) {
				return Number(val)
			}
		}
		return null
	}
	const getRate = (nameStr: string) =>
		rateOf(
			(mealRates || []).find(
				(r) => (r.meal_type || "").trim().toLowerCase() === nameStr
			)
		)

	// A custom plan has no breakfast, lunch or dinner to add up. Its rate is the
	// one stored against its own id, which is the only thing that names it — the
	// plan's name says nothing about what it costs to add.
	if (isCustomMealPlan({ ota_meal_plan: mealPlan })) {
		const planRate = rateOf(
			(mealRates || []).find(
				(r) =>
					r.ota_meal_plan != null && Number(r.ota_meal_plan) === mealPlan.id
			)
		)
		return planRate !== null
			? { isCalculable: true, addonCost: planRate }
			: { isCalculable: false, addonCost: null }
	}

	const mealPlanCategoryOrName = mealPlan.meal_category || mealPlan.name

	const bRate = getRate("breakfast")
	const lRate = getRate("lunch")
	const dRate = getRate("dinner")

	const code = (mealPlanCategoryOrName || "").trim().toUpperCase()

	if (code.startsWith("EP")) {
		return { isCalculable: true, addonCost: 0 }
	} else if (code.startsWith("CP")) {
		if (bRate !== null) {
			return { isCalculable: true, addonCost: bRate }
		}
		return { isCalculable: false, addonCost: null }
	} else if (code.startsWith("MAP")) {
		if (bRate !== null && (lRate !== null || dRate !== null)) {
			const maxLunchDinner = Math.max(
				lRate !== null ? lRate : -Infinity,
				dRate !== null ? dRate : -Infinity
			)
			return { isCalculable: true, addonCost: bRate + maxLunchDinner }
		}
		return { isCalculable: false, addonCost: null }
	} else if (code.startsWith("AP")) {
		if (bRate !== null && lRate !== null && dRate !== null) {
			return { isCalculable: true, addonCost: bRate + lRate + dRate }
		}
		return { isCalculable: false, addonCost: null }
	}

	const directRate = getRate(
		(mealPlanCategoryOrName || "").trim().toLowerCase()
	)
	if (directRate !== null) {
		return { isCalculable: true, addonCost: directRate }
	}

	const mapped = MEAL_TO_MEAL_TYPES[code]
	if (mapped) {
		if (mapped.length === 0) return { isCalculable: true, addonCost: 0 }
		let cost = 0
		for (const course of mapped) {
			const r = getRate(course)
			if (r === null) return { isCalculable: false, addonCost: null }
			cost += r
		}
		return { isCalculable: true, addonCost: cost }
	}

	return { isCalculable: false, addonCost: null }
}

export function calculateRawMealPlanRate(
	baseRateVal: number | string | null | undefined,
	baseMp: TOtaMealPlan | null,
	targetMp: TOtaMealPlan,
	childRateSameAsAdult: boolean,
	mealRates: {
		meal_type?: string | null
		ota_meal_plan?: number | null
		per_adult_rate_before_tax?: number | string | null
		per_child_rate_before_tax?: number | string | null
	}[],
	paxCount = 1,
	isChildCol = false
): number | null {
	if (!isNumeric(baseRateVal) || !baseMp) return null

	const useChildRate = isChildCol && !childRateSameAsAdult

	const baseAddon = calculateMealPlanAddon(baseMp, mealRates, useChildRate)
	const targetAddon = calculateMealPlanAddon(targetMp, mealRates, useChildRate)

	if (baseAddon.isCalculable && targetAddon.isCalculable) {
		const deltaPerAdult =
			(targetAddon.addonCost ?? 0) - (baseAddon.addonCost ?? 0)
		return Number(baseRateVal) + deltaPerAdult * paxCount
	}

	return null
}

// Each column's pax type is what decides how it is priced by default: the first
// CNB band is typed in and the rest follow it, CWEB follows AWEB. Read off the
// column's `paxType`, which is the backend's own classification of it.
export function getInitialColumnDerivations(
	cols: Pick<TRoomCol, "key" | "paxType">[]
): Record<
	string,
	{
		mode: "manual" | "derived"
		sourceColKey?: string
		multiplier?: number
		constant?: number
	}
> {
	const initial: Record<
		string,
		{
			mode: "manual" | "derived"
			sourceColKey?: string
			multiplier?: number
			constant?: number
		}
	> = {}

	const firstCnbKey = cols.find((c) => c.paxType === "CNB")?.key
	const awebKey = cols.find((c) => c.paxType === "AWEB")?.key

	// A column can only derive from one that actually exists. Falling back to a
	// literal key name instead left the column pointing at nothing: the sync step
	// found no such column and never filled the cell, while `isDerivedRate` still
	// treated it as derived and skipped validating it — a permanently blank rate
	// that passed the submit gate. With no source, the column stays manual.
	cols.forEach((col) => {
		if (col.paxType === "CNB" && col.key !== firstCnbKey && firstCnbKey) {
			initial[col.key] = {
				mode: "derived",
				sourceColKey: firstCnbKey,
				multiplier: 1,
				constant: 0,
			}
		} else if (col.paxType === "CWEB" && awebKey) {
			initial[col.key] = {
				mode: "derived",
				sourceColKey: awebKey,
				multiplier: 1,
				constant: 0,
			}
		} else {
			initial[col.key] = { mode: "manual" }
		}
	})

	return initial
}

// The cells the grid fills in on its own — derived columns on each room's base
// meal plan row, and every other meal plan row off that one — settled against
// the rates as they stand. Returns `rates` itself when nothing moved, so a
// caller can tell a settled grid from one that changed. A cell the user has
// overridden (`is_manual`) is never touched.
//
// Chains are walked unrounded and only the settled values are rounded on the
// way out, so a derivation of a derivation does not compound rounding.
export function deriveGridRates({
	rates,
	geometry: {
		activeRoomTypes,
		visibleRows,
		roomCols,
		lowerCols,
		numLowerCols,
		itemsPerRoom,
		baseMealPlan,
	},
	configureLowerOccupancy,
	columnDerivations,
	childRateSameAsAdult,
	mealRates,
	roundingValue,
}: {
	rates: TGridRate[]
	geometry: Pick<
		TGridGeometry,
		| "activeRoomTypes"
		| "visibleRows"
		| "roomCols"
		| "lowerCols"
		| "numLowerCols"
		| "itemsPerRoom"
		| "baseMealPlan"
	>
	configureLowerOccupancy: boolean
	columnDerivations: Record<string, TColumnDerivation>
	childRateSameAsAdult: boolean
	mealRates: IRateBlockSchema["meal_rates"] | undefined
	roundingValue: number | null
}): TGridRate[] {
	if (!rates.length) return rates

	const patches = new Map<number, Partial<TGridRate>>()
	const patch = (cellIdx: number, fields: Partial<TGridRate>) =>
		patches.set(cellIdx, { ...patches.get(cellIdx), ...fields })

	const unroundedRates = new Map<number, number | null>()
	rates.forEach((r, idx) => {
		unroundedRates.set(
			idx,
			isNumeric(r?.rate_before_tax) ? Number(r.rate_before_tax) : null
		)
	})

	const lowerOffset = configureLowerOccupancy ? numLowerCols : 0

	// Compared through `toNum`: a cell the user has typed into holds its value as
	// a string, and a strict compare against the number the formula produced
	// reports every settled cell as moved.
	const patchSettledRate = (cellIdx: number) => {
		const unrounded = unroundedRates.get(cellIdx)
		const derivedVal = applyCeilingRounding(unrounded, roundingValue)
		const unroundedVal = unroundedRateFor(unrounded, roundingValue)
		if (toNum(rates[cellIdx]?.rate_before_tax) !== derivedVal) {
			patch(cellIdx, { rate_before_tax: derivedVal })
		}
		if (toNum(rates[cellIdx]?.rate_before_tax_unrounded) !== unroundedVal) {
			patch(cellIdx, { rate_before_tax_unrounded: unroundedVal })
		}
	}

	activeRoomTypes.forEach((rt) => {
		const rtBaseOcc = getRoomBaseOccupancy(rt)

		const baseRowIndex = visibleRows.findIndex(
			(r) => r.rt.id === rt.id && r.isBase
		)
		if (baseRowIndex === -1) return

		let passChanged = true
		let passCount = 0
		const maxPasses = roomCols.length + 1

		while (passChanged && passCount < maxPasses) {
			passChanged = false
			passCount++

			for (let colIndex = 0; colIndex < roomCols.length; colIndex++) {
				const col = roomCols[colIndex]
				const derivation = columnDerivations[col.key]
				if (
					derivation &&
					derivation.mode === "derived" &&
					derivation.sourceColKey
				) {
					const colOffset = lowerOffset + 1 + colIndex
					const targetCellIdx = baseRowIndex * itemsPerRoom + colOffset

					if (!rates[targetCellIdx]?.is_manual) {
						let sourceColOffset = -1
						if (derivation.sourceColKey === "base") {
							sourceColOffset = lowerOffset
						} else {
							const srcIdx = roomCols.findIndex(
								(c) => c.key === derivation.sourceColKey
							)
							if (srcIdx !== -1) {
								sourceColOffset = lowerOffset + 1 + srcIdx
							} else if (configureLowerOccupancy) {
								const srcLowerIdx = lowerCols.findIndex(
									(c) => c.key === derivation.sourceColKey
								)
								if (srcLowerIdx !== -1) {
									sourceColOffset = srcLowerIdx
								}
							}
						}

						if (sourceColOffset !== -1) {
							const sourceCellIdx =
								baseRowIndex * itemsPerRoom + sourceColOffset
							const sourceVal = unroundedRates.get(sourceCellIdx)

							let unroundedVal: number | null = null
							if (isNumeric(sourceVal)) {
								const mult = derivation.multiplier ?? 1
								const constVal = derivation.constant ?? 0
								// Normalised before it goes back into the map: this value is
								// the source of the next link in the chain, and a raw product
								// carries float artifacts forward — `(1000 * 0.33) * 1.1` is
								// `363.00000000000006`.
								unroundedVal = normalizeDerivedRate(
									Number(sourceVal) * mult + constVal
								)
							}

							if (unroundedRates.get(targetCellIdx) !== unroundedVal) {
								unroundedRates.set(targetCellIdx, unroundedVal)
								passChanged = true
							}
						}
					}
				}
			}
		}

		const subRows = baseMealPlan
			? visibleRows
					.map((r, idx) => ({ ...r, rowIndex: idx }))
					.filter((r) => r.rt.id === rt.id && !r.isBase)
			: []

		if (baseMealPlan && subRows.length > 0) {
			for (let colIdx = 0; colIdx < itemsPerRoom; colIdx++) {
				const baseCellIdx = baseRowIndex * itemsPerRoom + colIdx
				const unroundedBaseRateVal = unroundedRates.get(baseCellIdx)

				const paxCount = getPaxCountForColOffset(
					colIdx,
					rtBaseOcc,
					lowerCols,
					roomCols,
					configureLowerOccupancy
				)

				const roomColIdx = colIdx - lowerOffset - 1
				const colPaxType =
					roomColIdx >= 0 && roomColIdx < roomCols.length
						? roomCols[roomColIdx].paxType
						: null
				const isChildCol = colPaxType === "CNB" || colPaxType === "CWEB"

				subRows.forEach((subRow) => {
					const targetCellIdx = subRow.rowIndex * itemsPerRoom + colIdx

					if (rates[targetCellIdx]?.is_manual) return

					const unroundedVal = calculateRawMealPlanRate(
						unroundedBaseRateVal,
						baseMealPlan,
						subRow.mp,
						childRateSameAsAdult,
						mealRates || [],
						paxCount,
						isChildCol
					)

					unroundedRates.set(targetCellIdx, normalizeDerivedRate(unroundedVal))
				})
			}
		}

		for (let colIndex = 0; colIndex < roomCols.length; colIndex++) {
			const col = roomCols[colIndex]
			const derivation = columnDerivations[col.key]
			if (
				derivation &&
				derivation.mode === "derived" &&
				derivation.sourceColKey
			) {
				const targetCellIdx =
					baseRowIndex * itemsPerRoom + lowerOffset + 1 + colIndex
				if (!rates[targetCellIdx]?.is_manual) {
					patchSettledRate(targetCellIdx)
				}
			}
		}

		subRows.forEach((subRow) => {
			for (let colIdx = 0; colIdx < itemsPerRoom; colIdx++) {
				const targetCellIdx = subRow.rowIndex * itemsPerRoom + colIdx

				// `continue`, not `return`: returning would abandon every remaining
				// column of this sub-row — freezing them on stale rates the moment the
				// user overrides one cell.
				if (rates[targetCellIdx]?.is_manual) continue

				patchSettledRate(targetCellIdx)
				// A sub-row cell is only ever this room type and this meal plan, so
				// these are settled after the first sync.
				if (
					Number(rates[targetCellIdx]?.ota_meal_plan) !== Number(subRow.mp.id)
				) {
					patch(targetCellIdx, { ota_meal_plan: subRow.mp.id })
				}
				if (Number(rates[targetCellIdx]?.ota_room_type) !== Number(rt.id)) {
					patch(targetCellIdx, { ota_room_type: rt.id })
				}
			}
		})
	})

	if (!patches.size) return rates
	return rates.map((cell, idx) =>
		patches.has(idx) ? { ...cell, ...patches.get(idx) } : cell
	)
}

// Maps a rate table column key to the occupancy key used in the API payload.
// Every column is keyed by the occupancy the backend named it with — "AWEB",
// "A1", "CNB (6-12)" — so BASE, which the grid keys "base", is the only one
// that has ever needed mapping.
export function toPaxOccupancy(colKey: string): string {
	const key = (colKey || "").trim()
	return key.toLowerCase() === "base" ? "BASE" : key
}

// Inverse of toPaxOccupancy, mapping an API occupancy back to its column key
export function fromPaxOccupancy(occupancy: string): string {
	const key = (occupancy || "").trim()
	return key.toUpperCase() === "BASE" ? "base" : key
}

// Decimal amounts and derivation factors arrive as strings ("500.00", "2.5",
// "-230"); the forms work in numbers throughout.
function toNumberOrNull(value: string | null) {
	return value === null ? null : Number(value)
}

// Both policy kinds are read through the same handful of helpers below: they
// describe their head the same way, so nothing here branches on the kind.
export type TAnyPolicy = TCancellationPolicy | TPaymentPolicy
export type TAnyPolicyDetail = TCancellationPolicyDetail | TPaymentPolicyDetail

// The head a policy version belongs to.
export function policyHeadId(
	policy: TAnyPolicy | null | undefined
): number | null {
	const id = policy?.head_id
	return id != null ? Number(id) : null
}

export type TPolicyKind = "cancellation" | "payment"

export function policyKindLabel(kind: TPolicyKind) {
	return kind === "cancellation" ? "Cancellation Policy" : "Payment Policy"
}

// A stable colour per policy, so the same policy reads as the same policy
// wherever it appears in the sheet. Left off for now — will be enabled later.
// export function policyColor(policy: TAnyPolicy | null | undefined): string {
// 	const headId = policyHeadId(policy)
// 	if (!headId) return "transparent"
// 	const hash = (headId * 2654435761) >>> 0
// 	return `#${(hash & 0xffffff).toString(16).padStart(6, "0").toUpperCase()}`
// }

export function policyHeadName(
	policy: TAnyPolicy | null | undefined
): string | undefined {
	return policy?.head_name
}

function findPolicyHead(
	policy: TAnyPolicy | null | undefined,
	heads: TAnyPolicyDetail[] | undefined
): TAnyPolicyDetail | undefined {
	if (!policy || !heads?.length) return undefined
	const headId = policyHeadId(policy)
	if (headId) {
		const byHead = heads.find((head) => Number(head.id) === headId)
		if (byHead) return byHead
	}
	// A policy resolved off the sheet's bare version id knows no head, and that id
	// identifies a head only while it is that head's latest.
	return heads.find(
		(head) => Number(head.latest_policy?.id) === Number(policy.id)
	)
}

// Stamps a policy version with the head it belongs to. The listing keeps the
// name on the head, and a `latest_policy` can come back with neither the name
// nor the back-pointer to it — `head_name` is optional and is not always served.
// Without this such a policy has no name to render and no head id to match its
// own option by, so the select shows an empty box over a perfectly good value.
export function withPolicyHead<T extends TAnyPolicy>(
	policy: T,
	head: TAnyPolicyDetail | undefined
): T {
	if (!head) return policy
	const headId = Number(head.id)
	// Left by identity when it already knows its head, so a memo'd consumer does
	// not re-render for a rewrite that changes nothing
	if (policyHeadId(policy) === headId && policyHeadName(policy) === head.name) {
		return policy
	}
	return { ...policy, head_id: headId, head_name: head.name }
}

// Whether a sheet is pinned to a version its head has since moved past — the
// "Update to Latest (vN)" state.
export function isPolicyStale(
	policy: TAnyPolicy | null | undefined,
	heads: TAnyPolicyDetail[] | undefined
): boolean {
	const head = findPolicyHead(policy, heads)
	if (!head?.latest_policy || !policy) return false
	return Number(head.latest_policy.version) > Number(policy.version)
}

// A saved sheet's policy carries its head's latest version, so whether the pin
// is stale reads off the policy itself, without the listings.
export function isRateSheetPolicyStale(policy: TRateSheetPolicy): boolean {
	return policy.version < policy.latest_version
}

// The version "Update to Latest" moves a pinned policy to.
export function latestPolicyFor<T extends TAnyPolicy>(
	policy: T | null | undefined,
	heads: TAnyPolicyDetail[] | undefined
): T | null {
	const head = findPolicyHead(policy, heads)
	if (!head?.latest_policy) return null
	return withPolicyHead(head.latest_policy as T, head)
}

// The latest version of a head the user just picked, ready to be held as the
// selected policy.
export function pickPolicyHead<T extends TAnyPolicy>(
	headId: number | null | undefined,
	heads: TAnyPolicyDetail[] | undefined
): T | null {
	if (!headId) return null
	const head = heads?.find((entry) => Number(entry.id) === Number(headId))
	if (!head?.latest_policy) return null
	return withPolicyHead(head.latest_policy as T, head)
}

// The policy a sheet is pinned to, named by the sheet itself. When the listings
// are in hand and the pin is its head's latest, the listing's copy is used
// instead, as that one carries the rules.
export function resolveBlockPolicy<T extends TAnyPolicy>(
	policy: TRateSheetPolicy,
	heads: TAnyPolicyDetail[] | undefined
): T {
	const head = heads?.find((entry) => entry.id === policy.head_id)
	if (head && head.latest_policy.id === policy.id) {
		return withPolicyHead(head.latest_policy as T, head)
	}
	return {
		...policy,
		rules: [],
		rule_sets: [],
	} as unknown as T
}

// Pushes the hotel level policies down onto the blocks that did not override
// them. A block holds its own copy so it can be read without the hotel in hand,
// which means a change made in step 1 leaves every non-overriding block behind
// until this runs. Blocks that did override — `_is_manual` — are left alone, and
// a block already holding the right policies is returned by identity so a memo'd
// rate card does not re-render.
export function applyHotelPoliciesToBlock(
	block: IRateBlockSchema,
	hotelPolicy: TCancellationPolicy | null,
	hotelPaymentPolicy: TPaymentPolicy | null
): IRateBlockSchema {
	const nextCancellation = block.cancellation_policy_is_manual
		? block.cancellation_policy
		: hotelPolicy || null
	const nextPayment = block.payment_policy_is_manual
		? block.payment_policy
		: hotelPaymentPolicy || null
	if (
		nextCancellation === block.cancellation_policy &&
		nextPayment === block.payment_policy
	) {
		return block
	}
	return {
		...block,
		cancellation_policy: nextCancellation,
		payment_policy: nextPayment,
	}
}

export function applyHotelPoliciesToSeasons(
	seasons: ISeasonSchema[] | undefined,
	hotelPolicies: THotelPolicyMaps["hotelPolicies"] | undefined,
	hotelPaymentPolicies: THotelPolicyMaps["hotelPaymentPolicies"] | undefined
): ISeasonSchema[] {
	if (!seasons?.length) return seasons || []
	return seasons.map((season) => {
		const blocks = (season.rate_blocks || []).map((block) =>
			applyHotelPoliciesToBlock(
				block,
				hotelPolicies?.[Number(block.hotel)] || null,
				hotelPaymentPolicies?.[Number(block.hotel)] || null
			)
		)
		const changed = blocks.some(
			(block, idx) => block !== season.rate_blocks[idx]
		)
		return changed ? { ...season, rate_blocks: blocks } : season
	})
}

// The hotel's default head id, however this particular response carried it:
// /hotels/:hotelId nests the full head object, the /hotels list endpoint
// sends just the id instead.
function hotelDefaultHeadId(
	head: TPolicyHeadSummary | null | undefined,
	headId: number | null | undefined
): number | null {
	if (head) return Number(head.id)
	return headId != null ? Number(headId) : null
}

// Seeds a hotel's policy maps from its own default policy heads
// (IHotel.cancellation_policy_head(_id) / payment_policy_head(_id)) wherever
// no policy is selected yet. Never touches a hotel that already has one — a
// manual pick or a value loaded from an existing sheet is left alone.
// Returns the input maps by identity when nothing needed filling.
export function applyHotelDefaultPolicies(
	hotels: IHotel[],
	hotelPolicies: THotelPolicyMaps["hotelPolicies"] | undefined,
	hotelPaymentPolicies: THotelPolicyMaps["hotelPaymentPolicies"] | undefined,
	policyHeads: TPolicyHeads
): THotelPolicyMaps {
	let policies = hotelPolicies || {}
	let paymentPolicies = hotelPaymentPolicies || {}
	hotels.forEach((hotel) => {
		const hotelId = Number(hotel.id)
		const cancellationHeadId = hotelDefaultHeadId(
			hotel.cancellation_policy_head,
			hotel.cancellation_policy_head_id
		)
		if (policies[hotelId] == null && cancellationHeadId) {
			const defaultPolicy = pickPolicyHead<TCancellationPolicy>(
				cancellationHeadId,
				policyHeads.cancellation
			)
			if (defaultPolicy) {
				if (policies === hotelPolicies) policies = { ...policies }
				policies[hotelId] = defaultPolicy
			}
		}
		const paymentHeadId = hotelDefaultHeadId(
			hotel.payment_policy_head,
			hotel.payment_policy_head_id
		)
		if (paymentPolicies[hotelId] == null && paymentHeadId) {
			const defaultPayment = pickPolicyHead<TPaymentPolicy>(
				paymentHeadId,
				policyHeads.payment
			)
			if (defaultPayment) {
				if (paymentPolicies === hotelPaymentPolicies)
					paymentPolicies = { ...paymentPolicies }
				paymentPolicies[hotelId] = defaultPayment
			}
		}
	})
	return { hotelPolicies: policies, hotelPaymentPolicies: paymentPolicies }
}

type TApiResponseToFormDataOptions = {
	name?: string
	policyHeads?: TPolicyHeads
}

// Inverse of `getPayloadData` — the API returns the payload back with its ids
// expanded into objects, so this reads a saved sheet into the same form values
// that produced it. Everything a block was priced with (its rooms, its pax type
// columns and their derivations, its meal plans) comes off the block itself
// rather than being re-derived from the hotel, which is what makes a saved
// sheet render and edit as it was saved.
export function apiResponseToFormData(
	data: TRateSheet,
	options?: TApiResponseToFormDataOptions
): INewRateSheetFormSchema {
	const hotels = data.hotels.map((entry) => entry.hotel)
	const hotelPolicies: Record<number, TCancellationPolicy> = {}
	const hotelPaymentPolicies: Record<number, TPaymentPolicy> = {}
	data.hotels.forEach((entry) => {
		hotelPolicies[entry.hotel.id] = resolveBlockPolicy<TCancellationPolicy>(
			entry.cancellation_policy,
			options?.policyHeads?.cancellation
		)
		hotelPaymentPolicies[entry.hotel.id] = resolveBlockPolicy<TPaymentPolicy>(
			entry.payment_policy,
			options?.policyHeads?.payment
		)
	})

	return {
		name: options?.name ?? "",
		hotels,
		hotelPolicies,
		hotelPaymentPolicies,
		seasons: data.seasons.map(
			(season): INewRateSheetFormSchema["seasons"][number] => ({
				_uid: `season_${season.id}`,
				name: season.name,
				intervals: season.intervals.map((interval) => ({
					from: parseDate(interval.from),
					to: parseDate(interval.to),
					days: getBinaryToDays(interval.weekdays_bitmap_str),
				})),
				rate_blocks: season.blocks.map((block) => ({
					hotel: block.hotel.id,
					cancellation_policy: block.cancellation_policy_is_manual
						? resolveBlockPolicy<TCancellationPolicy>(
								block.cancellation_policy,
								options?.policyHeads?.cancellation
							)
						: hotelPolicies[block.hotel.id],
					cancellation_policy_is_manual: block.cancellation_policy_is_manual,
					payment_policy: block.payment_policy_is_manual
						? resolveBlockPolicy<TPaymentPolicy>(
								block.payment_policy,
								options?.policyHeads?.payment
							)
						: hotelPaymentPolicies[block.hotel.id],
					payment_policy_is_manual: block.payment_policy_is_manual,
					rounding_point: roudingOptions.find(
						(option) => block.rounding_point === option.id
					) ?? { id: null, name: "None" },
					pricing_type:
						block.pricing_type === "OBP"
							? { id: "OBP", name: "Occupancy Rate" }
							: { id: "RBP", name: "Room Rate" },
					child_meal_rate_same_as_adult: block.child_meal_rate_same_as_adult,
					is_lower_occupancy_rate_configured:
						block.is_lower_occupancy_rate_configured,
					meal_plans: block.meal_plans
						.map((mp) => ({
							ota_meal_plan: mp.ota_meal_plan,
							category:
								mp.meal_category || mp.ota_meal_plan.meal_category || undefined,
							is_base: mp.is_base,
							includes_all_room_types: mp.includes_all_room_types,
							ota_room_types: mp.ota_room_types,
						}))
						.sort(compareMealPlanEntriesByPriority),
					meal_rates: block.meal_rates.map((mr) => ({
						meal_type: mr.meal_type,
						...(mr.meal_type == null && mr.ota_meal_plan
							? { ota_meal_plan: mr.ota_meal_plan.id }
							: {}),
						per_adult_rate_before_tax: Number(mr.per_adult_rate_before_tax),
						per_child_rate_before_tax: Number(mr.per_child_rate_before_tax),
					})),
					rooms: block.rooms.map((room) => ({
						ota_room_type: room.ota_room_type.id,
						base_adult_occupancy: room.base_adult_occupancy,
						base_child_occupancy: room.base_child_occupancy,
						max_occupancy: room.max_occupancy,
						max_adult_occupancy: room.max_adult_occupancy,
						max_child_occupancy: room.max_child_occupancy,
					})),
					pax_types: block.pax_types.map((paxType) => ({
						occupancy: paxType.occupancy,
						pax_type: paxType.pax_type,
						pax_count: paxType.pax_count,
						start_age: paxType.start_age,
						end_age: paxType.end_age,
						derived_from_occupancy: paxType.derived_from_occupancy,
						derivation_multiplier: toNumberOrNull(
							paxType.derivation_multiplier
						),
						derivation_adder: toNumberOrNull(paxType.derivation_adder),
					})),
					rates: block.rates.map((r) => ({
						ota_room_type: r.ota_room_type.id,
						// The response speaks payload occupancies, the grid column keys
						occupancy: fromPaxOccupancy(r.occupancy),
						ota_meal_plan: r.ota_meal_plan.id,
						rate_before_tax: Number(r.rate_before_tax),
						rate_before_tax_unrounded: toNumberOrNull(
							r.rate_before_tax_unrounded
						),
						is_manual: r.is_manual,
					})),
				})),
			})
		),
	}
}

// A sheet as the detail endpoint returns it — the head, whose latest version is
// the one every screen renders.
export function headToFormData(
	head: TRateSheetHead,
	options?: Omit<TApiResponseToFormDataOptions, "name">
): INewRateSheetFormSchema {
	return apiResponseToFormData(head.latest_rate_sheet, {
		...options,
		name: head.name,
	})
}

// Bit order of `weekdays_bitmap_str`: index 0 is Sunday.
const WEEK_DAYS: ReadonlyArray<TDay> = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
]

function getDaysToBinary({ days }: { days: TDay[] }): string {
	return WEEK_DAYS.map((day) => (days.includes(day) ? "1" : "0")).join("")
}

export function getBinaryToDays(binary: string | null | undefined): TDay[] {
	if (!binary) return []
	return WEEK_DAYS.filter((_, index) => binary[index] === "1")
}

// Shared by the list's "Create Rate Sheet" dialog and the wizard's name dialog
export const rateSheetNameValidationSchema = Validator.object().shape({
	name: Validator.string()
		.min(4, "Name must be at least 4 characters")
		.max(191, "Name must be 191 characters or fewer")
		.required("Name is required"),
})

// Util to form payload from form's data
export function getPayloadData(values: INewRateSheetFormSchema): TPayload {
	const hotels = values.hotels || []
	const hotelPolicies = values.hotelPolicies || {}
	const hotelPaymentPolicies = values.hotelPaymentPolicies || {}
	const paymentPolicyOf = (hotelId: number) =>
		Number(hotelPaymentPolicies[hotelId]?.id ?? 0)

	return {
		name: values.name,
		hotels: hotels.length
			? hotels.map((hotel) => {
					const hotelId = Number(hotel.id)
					const cancellationPolicy = hotelPolicies[hotelId]
					return {
						hotel: hotelId,
						cancellation_policy: Number(cancellationPolicy?.id ?? 0),
						payment_policy: paymentPolicyOf(hotelId),
					}
				})
			: Object.entries(hotelPolicies).map(([hotelId, cancellationPolicy]) => ({
					hotel: Number(hotelId),
					cancellation_policy: Number(cancellationPolicy?.id ?? 0),
					payment_policy: paymentPolicyOf(Number(hotelId)),
				})),
		seasons: values.seasons.map((season) => ({
			name: season.name,
			intervals: season.intervals.map((interval) => ({
				from: interval.from ? formatDate(interval.from, "YYYY-MM-DD") : "",
				to: interval.to ? formatDate(interval.to, "YYYY-MM-DD") : "",
				weekdays_bitmap: parseInt(getDaysToBinary({ days: interval.days }), 2),
				weekdays_bitmap_str: getDaysToBinary({ days: interval.days }),
			})),
			blocks: (season.rate_blocks || []).map((block) => {
				const hotelId = Number(block.hotel)
				const hotel = hotels.find((h) => Number(h.id) === hotelId)
				// A block that did not override a policy sells under its hotel's, so
				// the hotel's value is what goes out for it — the block's own copy is
				// only a mirror of it and can be behind a change made in step 1.
				const cancellationIsManual = Boolean(
					block.cancellation_policy_is_manual
				)
				const paymentIsManual = Boolean(block.payment_policy_is_manual)

				return {
					hotel: hotelId,
					rounding_point: block.rounding_point?.id ?? null,
					pricing_type: block.pricing_type?.id ?? "RBP",
					child_meal_rate_same_as_adult: block.child_meal_rate_same_as_adult
						? 1
						: 0,
					is_lower_occupancy_rate_configured:
						block.is_lower_occupancy_rate_configured ? 1 : 0,
					cancellation_policy: cancellationIsManual
						? Number(block.cancellation_policy?.id ?? 0)
						: Number(hotelPolicies[hotelId]?.id ?? 0),
					cancellation_policy_is_manual: cancellationIsManual ? 1 : 0,
					payment_policy: paymentIsManual
						? Number(block.payment_policy?.id ?? 0)
						: paymentPolicyOf(hotelId),
					payment_policy_is_manual: paymentIsManual ? 1 : 0,
					meal_plans: (block.meal_plans || []).map((mp) => ({
						ota_meal_plan: Number(mp.ota_meal_plan.id),
						category: mp.category || mp.ota_meal_plan.meal_category || "",
						is_base: mp.is_base ? 1 : 0,
						includes_all_room_types: mp.includes_all_room_types ? 1 : 0,
						ota_room_types: (mp.ota_room_types || []).map((rt) =>
							Number(rt.id)
						),
					})),
					meal_rates: (block.meal_rates || []).map((mr) => ({
						...(mr.ota_meal_plan != null
							? { meal_type: null, ota_meal_plan: Number(mr.ota_meal_plan) }
							: { meal_type: mr.meal_type }),
						per_adult_rate_before_tax:
							mr.per_adult_rate_before_tax != null
								? Number(mr.per_adult_rate_before_tax)
								: null,
						per_child_rate_before_tax:
							mr.per_child_rate_before_tax != null
								? Number(mr.per_child_rate_before_tax)
								: null,
					})),
					rooms: block.rooms?.length
						? block.rooms
						: buildRooms(hotel?.ota_room_types),
					pax_types: block.pax_types || [],
					rates: (block.rates || []).map((r) => ({
						ota_room_type: Number(r.ota_room_type),
						occupancy: toPaxOccupancy(r.occupancy),
						ota_meal_plan: Number(r.ota_meal_plan),
						rate_before_tax:
							r.rate_before_tax != null ? Number(r.rate_before_tax) : null,
						// Normalised here rather than trusted from the block: a block the
						// user never opened is passed straight through from the response,
						// and this is the one boundary every rate leaves through.
						rate_before_tax_unrounded: r.is_manual
							? null
							: r.rate_before_tax_unrounded != null
								? Number(r.rate_before_tax_unrounded)
								: null,
						is_manual: r.is_manual ? 1 : 0,
					})),
				}
			}),
		})),
	}
}

export const MEAL_TO_MEAL_TYPES: Record<string, string[]> = {
	EP: [],
	CP: ["breakfast"],
	MAP: ["breakfast", "lunch", "dinner"],
	AP: ["breakfast", "lunch", "dinner"],
}

// What a block prices meals against: breakfast, lunch or dinner by name, or a
// custom meal plan by id. `name` is what the row is labelled with; only a custom
// plan carries `ota_meal_plan`.
export type TMealCourse = { name: string; ota_meal_plan?: number }

const MEAL_COURSE_ORDER = ["breakfast", "lunch", "dinner"]

// A plan with no meal category has no breakfast/lunch/dinner to break down into,
// which is what the backend's `meal_type` can express.
export function isCustomMealPlan(
	mealPlan: Pick<
		IRateBlockSchema["meal_plans"][number],
		"ota_meal_plan" | "category"
	>
): boolean {
	return (
		Boolean(mealPlan.ota_meal_plan) &&
		!(mealPlan.ota_meal_plan.meal_category || mealPlan.category)
	)
}

// Identifies the course a meal rate row belongs to, so a rate can be matched back
// to its course by something sturdier than a display name.
export function mealCourseKey(course: TMealCourse): string {
	return course.ota_meal_plan != null
		? `plan:${course.ota_meal_plan}`
		: course.name.toLowerCase()
}

export function mealRateKey(
	mealRate: Pick<TPayloadMealRate, "meal_type" | "ota_meal_plan"> | undefined
): string | null {
	if (mealRate?.ota_meal_plan != null) return `plan:${mealRate.ota_meal_plan}`
	return mealRate?.meal_type ? mealRate.meal_type.toLowerCase() : null
}

export function getMealCourses(
	selectedMealPlans: IRateBlockSchema["meal_plans"] | undefined
): TMealCourse[] {
	const standard = new Set<string>()
	const custom = new Map<number, TMealCourse>()
	if (selectedMealPlans) {
		for (const mt of selectedMealPlans) {
			if (!mt) continue
			if (isCustomMealPlan(mt)) {
				const id = Number(mt.ota_meal_plan.id)
				custom.set(id, { name: mt.ota_meal_plan.name, ota_meal_plan: id })
				continue
			}
			const code = mt.ota_meal_plan?.name
			const category = mt.ota_meal_plan?.meal_category || mt.category
			const mappedCourses =
				(code && MEAL_TO_MEAL_TYPES[code]) ||
				(category && MEAL_TO_MEAL_TYPES[category])
			if (Array.isArray(mappedCourses)) {
				mappedCourses.forEach((c) => standard.add(c))
			}
		}
	}
	return [
		...MEAL_COURSE_ORDER.filter((name) => standard.has(name)).map((name) => ({
			name,
		})),
		...Array.from(custom.values()).sort(
			(a, b) =>
				a.name.localeCompare(b.name) ||
				Number(a.ota_meal_plan) - Number(b.ota_meal_plan)
		),
	]
}

// Validates the meal rates of a rate block. Shared by the rate sheet form and
// the hotel calendar's Add Rates form so a rule change applies to both.
export function validateBlockMealRates(
	values: Pick<
		IRateBlockSchema,
		"meal_rates" | "meal_plans" | "child_meal_rate_same_as_adult"
	>
) {
	const errors: Record<string, unknown> = {}
	if (values.meal_rates) {
		const activeCourses = getMealCourses(values.meal_plans)
		const mealRatesErrors: Record<string, string>[] = []
		let hasMealRateError = false
		values.meal_rates.forEach((mr, idx) => {
			if (idx >= activeCourses.length) return
			if (!mr) return
			if (!isNumeric(mr.per_adult_rate_before_tax)) {
				mealRatesErrors[idx] = { per_adult_rate_before_tax: "Required" }
				hasMealRateError = true
			} else if (Number(mr.per_adult_rate_before_tax) < 0) {
				mealRatesErrors[idx] = {
					per_adult_rate_before_tax: "Must be a positive value",
				}
				hasMealRateError = true
			}
			if (!values.child_meal_rate_same_as_adult) {
				if (!isNumeric(mr.per_child_rate_before_tax)) {
					mealRatesErrors[idx] = {
						...(mealRatesErrors[idx] || {}),
						per_child_rate_before_tax: "Required",
					}
					hasMealRateError = true
				} else if (Number(mr.per_child_rate_before_tax) < 0) {
					mealRatesErrors[idx] = {
						...(mealRatesErrors[idx] || {}),
						per_child_rate_before_tax: "Must be a positive value",
					}
					hasMealRateError = true
				}
			}
		})
		if (hasMealRateError) {
			errors.meal_rates = mealRatesErrors
		}
	}
	return errors
}

export function syncMealRates(
	selectedMealPlans: IRateBlockSchema["meal_plans"] | undefined,
	currentMealRates: IRateBlockSchema["meal_rates"] | undefined
): IRateBlockSchema["meal_rates"] {
	const courses = getMealCourses(selectedMealPlans)
	const existingMap = new Map<string, IRateBlockSchema["meal_rates"][number]>()

	if (currentMealRates) {
		for (const mr of currentMealRates) {
			const key = mealRateKey(mr)
			if (mr && key) existingMap.set(key, mr)
		}
	}

	return courses.map((course) => {
		const identity =
			course.ota_meal_plan != null
				? { meal_type: null, ota_meal_plan: course.ota_meal_plan }
				: { meal_type: course.name }
		const existing = existingMap.get(mealCourseKey(course))
		if (existing) {
			return { ...existing, ...identity }
		}
		return {
			...identity,
			per_adult_rate_before_tax: null,
			per_child_rate_before_tax: null,
		}
	})
}

// Meal rates price the gap between the base plan and the others — the meal plan
// sub-rows derive from them. With only the base plan selected the grid has no
// sub-rows, so the rates feed nothing and the block carries none at all. Falls
// back to the hotel's plans for a block that was never opened, the same fallback
// `getExpectedMealCourses` makes.
export function blockNeedsMealRates(
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	hotel?: Pick<IHotel, "ota_meal_plans">
): boolean {
	const selected = (mealPlans || []).filter((mp) => mp?.ota_meal_plan)
	if (selected.length) return selected.length > 1
	return (hotel?.ota_meal_plans || []).length > 1
}

// The block's meal rates, gated on whether it needs any. `syncMealRates` keeps
// its own contract — courses in, one row out per course — and this is the single
// choke point the form and the payload go through.
export function syncBlockMealRates(
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	currentMealRates: IRateBlockSchema["meal_rates"] | undefined,
	hotel?: Pick<IHotel, "ota_meal_plans">
): IRateBlockSchema["meal_rates"] {
	if (!blockNeedsMealRates(mealPlans, hotel)) return []
	return syncMealRates(mealPlans, currentMealRates)
}

// The label of the course a meal rate row prices. A custom plan's row has no
// `meal_type` to read it off, so it is looked up by the row's plan id; the index
// is the last resort for a row that names neither.
function mealRateCourseName(
	mealRate: IRateBlockSchema["meal_rates"][number] | undefined,
	courses: TMealCourse[],
	idx: number
): string | undefined {
	const key = mealRateKey(mealRate)
	const course = key ? courses.find((c) => mealCourseKey(c) === key) : undefined
	return course?.name || mealRate?.meal_type || courses[idx]?.name
}

// The courses a block is expected to price. A block the user has never opened
// carries no meal plans of its own, so fall back to the hotel's OTA meal plans
// — the same fallback the rate grid performs when picking its base meal plan.
export function getExpectedMealCourses(
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	hotel: IHotel
): TMealCourse[] {
	if (mealPlans?.length) return getMealCourses(mealPlans)
	return getMealCourses(
		(hotel.ota_meal_plans || []).map((mp) => ({
			ota_meal_plan: mp,
			is_base: false,
			includes_all_room_types: true,
			ota_room_types: null,
		}))
	)
}

// ---------------------------------------------------------------------------
// Rate grid geometry
//
// The shape of the room rates grid — its rows, its columns and the stride
// between rows — is derived entirely from the hotel and the block's meal plans.
// Deriving it here rather than inside the grid component is what lets the same
// warnings be computed for a block that is not mounted, which every hotel x
// season other than the active one is.
// ---------------------------------------------------------------------------

export type TLowerCol = Pick<
	TRoomCol,
	"key" | "label" | "occupancy" | "paxType"
> & {
	occ: number
}

type TGridVisibleRow = {
	rt: THotelOtaRoomType
	mp: TOtaMealPlan
	isBase: boolean
}

export type TGridGeometry = {
	activeRoomTypes: THotelOtaRoomType[]
	baseMealPlan: TOtaMealPlan | null
	otherMealPlans: TOtaMealPlan[]
	lowerCols: TLowerCol[]
	numLowerCols: number
	roomCols: TRoomCol[]
	visibleRows: TGridVisibleRow[]
	itemsPerRoom: number
	aboveRowIndicesMap: Record<number, number>
	signature: string
}

// The identity of a grid's layout, as a string the grid can be keyed on.
//
// `rates` is addressed positionally, so index N only means the same cell for as
// long as every row and every column stays where it is. Counting them is not
// enough: a block mounts before its base meal plan is seeded, on rows that are
// one room type x every meal plan it is sold on, and settles on one base row per
// room type. Those two layouts can come out the same size — a hotel with three
// room types where only one takes all three of its meal plans gives three rows
// either way — and a size-only key lets that reshuffle through, leaving every
// cell index pointing at a different room and meal plan than it was seeded with.
function gridGeometrySignature(
	{
		visibleRows,
		lowerCols,
		roomCols,
	}: Pick<TGridGeometry, "visibleRows" | "lowerCols" | "roomCols">,
	configureLowerOccupancy: boolean
): string {
	const rows = visibleRows.map((row) => `${row.rt.id}:${row.mp.id}`).join("|")
	const cols = [
		...(configureLowerOccupancy ? lowerCols.map((col) => col.key) : []),
		"base",
		...roomCols.map((col) => col.key),
	].join("|")
	return `${configureLowerOccupancy ? 1 : 0}_${rows}_${cols}`
}

// One column per adult count a room can be sold below its own base occupancy.
// The backend sends these as the difference between a room type's
// lower-occupancy pax type list and its plain one — the extra entries are
// exactly the occupancies it can be sold down to.
export function buildLowerCols(
	activeRoomTypes: THotelOtaRoomType[]
): TLowerCol[] {
	const byCount = new Map<number, TLowerCol>()

	activeRoomTypes.forEach((rt) => {
		const own = new Set(
			(rt.pax_types || []).map((paxType) => paxType.occupancy)
		)
		for (const paxType of rt.pax_types_with_lower_occupancies_configuration ||
			[]) {
			if (own.has(paxType.occupancy)) continue
			const occ = paxCountOf(paxType)
			if (occ === null || occ <= 0 || byCount.has(occ)) continue
			const occupancy = (paxType.occupancy || "").trim()
			byCount.set(occ, {
				occ,
				key: occupancy,
				label: getOccupancyInfo(occ).label,
				occupancy,
				paxType: (paxType.pax_type || "A").trim().toUpperCase(),
			})
		}
	})

	return Array.from(byCount.values()).sort((a, b) => a.occ - b.occ)
}

// The extra pax columns a saved block was priced with, read off its own pax
// types instead of the hotel's configuration. A saved sheet has to show the
// columns it was priced with rather than the ones the hotel offers today — and
// the sheet response carries no OTA configuration to re-derive them from.
export function roomColsFromPaxTypes(
	paxTypes: TPaxTypeColumnSource[] | undefined
): TRoomCol[] {
	return (paxTypes || [])
		.filter((paxType) => {
			// BASE is a room's own occupancy, priced in its own column, and the "A"
			// pax types are the lower occupancies — neither is an extra pax column.
			const type = (paxType?.pax_type || "").trim().toUpperCase()
			return Boolean(paxType?.occupancy) && type !== "BASE" && type !== "A"
		})
		.map((paxType) => {
			const col = toRoomCol(paxType, true)
			// The saved ages are authoritative; `toRoomCol` only reads them back out
			// of the occupancy's own naming
			return {
				...col,
				startAge: paxType.start_age ?? col.startAge,
				endAge: paxType.end_age ?? col.endAge,
			}
		})
}

// The lower occupancy columns of a saved block — the "A1", "A2" pax types it
// priced rooms below their base occupancy at.
export function lowerColsFromPaxTypes(
	paxTypes: TPaxTypeColumnSource[] | undefined
): TLowerCol[] {
	const byCount = new Map<number, TLowerCol>()

	;(paxTypes || []).forEach((paxType) => {
		if ((paxType?.pax_type || "").trim().toUpperCase() !== "A") return
		const occ = paxCountOf(paxType)
		if (occ === null || occ <= 0 || byCount.has(occ)) return
		const occupancy = (paxType.occupancy || "").trim()
		byCount.set(occ, {
			occ,
			key: occupancy,
			label: getOccupancyInfo(occ).label,
			occupancy,
			paxType: "A",
		})
	})

	return Array.from(byCount.values()).sort((a, b) => a.occ - b.occ)
}

// Display order of the meal plans, by how much they include.
const MEAL_PLAN_PRIORITY_ORDER = ["CP", "MAP", "AP", "EP"]

// Order the default base meal plan is picked in when a block has not marked
// one. Deliberately NOT the display order above: the base plan is the one the
// other plans' rates are derived from, so the least inclusive plan is preferred
// and EP comes second rather than last.
export const DEFAULT_BASE_MEAL_PLAN_ORDER = ["CP", "EP", "MAP", "AP"]

// Ranks a meal plan for display order: CP, then MAP, then AP, then EP, then
// anything else (a blank or custom category) last. Ranked off `meal_category`,
// falling back to `name` when the category is blank or not one of the four.
function mealPlanPriorityRank(mp: {
	name?: string | null
	meal_category?: string | null
}): number {
	const category = (mp.meal_category || "").trim().toUpperCase()
	let idx = MEAL_PLAN_PRIORITY_ORDER.indexOf(category)
	if (idx === -1) {
		const name = (mp.name || "").trim().toUpperCase()
		idx = MEAL_PLAN_PRIORITY_ORDER.indexOf(name)
	}
	return idx === -1 ? MEAL_PLAN_PRIORITY_ORDER.length : idx
}

// CP -> MAP -> AP -> EP -> by id. The order meal plans are added to a rate
// sheet and the order their room-rate sub-rows appear in.
export function compareMealPlansByPriority(
	a: { id: number; name?: string | null; meal_category?: string | null },
	b: { id: number; name?: string | null; meal_category?: string | null }
): number {
	const rankDiff = mealPlanPriorityRank(a) - mealPlanPriorityRank(b)
	if (rankDiff !== 0) return rankDiff
	return Number(a.id) - Number(b.id)
}

// Same ordering, applied to a rate block's meal plan entries rather than raw
// OTA meal plan objects — the entry's own `category` snapshot is the fallback
// when the nested `ota_meal_plan` carries none.
export function compareMealPlanEntriesByPriority(
	a: {
		ota_meal_plan?: {
			id: number
			name?: string | null
			meal_category?: string | null
		} | null
		category?: string | null
	},
	b: {
		ota_meal_plan?: {
			id: number
			name?: string | null
			meal_category?: string | null
		} | null
		category?: string | null
	}
): number {
	return compareMealPlansByPriority(
		{
			id: Number(a.ota_meal_plan?.id ?? 0),
			name: a.ota_meal_plan?.name,
			meal_category: a.ota_meal_plan?.meal_category ?? a.category,
		},
		{
			id: Number(b.ota_meal_plan?.id ?? 0),
			name: b.ota_meal_plan?.name,
			meal_category: b.ota_meal_plan?.meal_category ?? b.category,
		}
	)
}

export function splitMealPlans(
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	hotel: IHotel
): {
	baseMealPlan: TOtaMealPlan | null
	otherMealPlans: TOtaMealPlan[]
} {
	let baseMealPlan: TOtaMealPlan | null = null
	const otherMealPlans: TOtaMealPlan[] = []

	if (mealPlans && Array.isArray(mealPlans) && mealPlans.length > 0) {
		const enabledTypes = mealPlans.filter((mt) => mt?.ota_meal_plan)
		const baseType = enabledTypes.find((mt) => mt.is_base === true)
		if (baseType?.ota_meal_plan) {
			baseMealPlan = baseType.ota_meal_plan
		}
		enabledTypes.forEach((mt) => {
			if (mt.ota_meal_plan && mt.ota_meal_plan !== baseMealPlan) {
				otherMealPlans.push(mt.ota_meal_plan)
			}
		})
	} else if (hotel.ota_meal_plans && hotel.ota_meal_plans.length > 0) {
		const baseFromHotel = hotel.ota_meal_plans.find(
			(mp) => (mp as { is_base?: boolean }).is_base === true
		)
		if (baseFromHotel) {
			baseMealPlan = baseFromHotel
			hotel.ota_meal_plans.forEach((mp) => {
				if (mp !== baseMealPlan) {
					otherMealPlans.push(mp)
				}
			})
		} else {
			otherMealPlans.push(...hotel.ota_meal_plans)
		}
	}

	otherMealPlans.sort(compareMealPlansByPriority)
	return { baseMealPlan, otherMealPlans }
}

// A saved block keeps the meal plans it was priced with, so a plan deleted from
// the hotel afterwards would stay in the grid with no switch left to turn it
// off. The block's base plan is the exception: the whole grid is priced off it,
// so it outlives the hotel's copy until the user picks another base.
//
// Returns `mealPlans` itself when nothing is dropped, so callers can tell a
// pruned list from an untouched one.
export function pruneDeletedMealPlanEntries(
	mealPlans: IRateBlockSchema["meal_plans"],
	hotelMealPlans: Pick<TOtaMealPlan, "id">[]
): IRateBlockSchema["meal_plans"] {
	const hotelMealPlanIds = new Set(hotelMealPlans.map((mp) => Number(mp.id)))
	const kept = mealPlans.filter(
		(entry) =>
			entry.is_base || hotelMealPlanIds.has(Number(entry.ota_meal_plan.id))
	)
	return kept.length === mealPlans.length ? mealPlans : kept
}

// `pruneDeletedMealPlanEntries` for a block that is never mounted. Nothing else
// would drop the removed plans' rates, so they would reach the payload.
//
// A hotel record without `ota_meal_plans` is the thin list record, not one with
// no plans, so it must not prune anything. Returns `block` itself when there is
// nothing to drop.
export function pruneDeletedMealPlans(
	block: IRateBlockSchema,
	hotel: Pick<IHotel, "ota_meal_plans">
): IRateBlockSchema {
	if (!hotel.ota_meal_plans) return block

	const mealPlans = pruneDeletedMealPlanEntries(
		block.meal_plans,
		hotel.ota_meal_plans
	)
	if (mealPlans === block.meal_plans) return block

	const keptIds = new Set(
		mealPlans.map((entry) => Number(entry.ota_meal_plan.id))
	)
	const removedIds = new Set(
		block.meal_plans
			.map((entry) => Number(entry.ota_meal_plan.id))
			.filter((id) => !keptIds.has(id))
	)
	return {
		...block,
		meal_plans: mealPlans,
		meal_rates: syncBlockMealRates(mealPlans, block.meal_rates),
		rates: block.rates.filter(
			(rate) => !removedIds.has(Number(rate.ota_meal_plan))
		),
	}
}

// The meal plans the "Applicable Meal Plans" list shows: the hotel's, plus the
// block's base plan when the hotel no longer has it, so the plan the grid is
// priced off is never in the tables without a row of its own.
export function mealPlansWithSavedBase(
	hotelMealPlans: THotelOtaMealPlan[],
	blockMealPlans: IRateBlockSchema["meal_plans"]
): THotelOtaMealPlan[] {
	const savedBase = blockMealPlans.find((entry) => entry.is_base)
	if (
		!savedBase ||
		hotelMealPlans.some(
			(mp) => Number(mp.id) === Number(savedBase.ota_meal_plan.id)
		)
	) {
		return hotelMealPlans
	}
	return [
		...hotelMealPlans,
		{ ...savedBase.ota_meal_plan, ota_room_types: [] },
	].sort(compareMealPlansByPriority)
}

// The room types a non-base meal plan is sold on. A plan restricted to a subset
// carries that subset itself; one that covers everything the plan supports has
// to be resolved against the hotel, because the block only records the flag.
//
// Reading it back off the block's own `ota_meal_plan` is not enough: a saved
// sheet echoes that object thin — id, name and category, no room types — so the
// wizard would filter and the detail view would not.
function mealPlanRoomTypes(
	mealPlan: TOtaMealPlan,
	entry: IRateBlockSchema["meal_plans"][number] | undefined,
	hotel: Pick<IHotel, "ota_meal_plans"> | undefined
): TOtaRoomType[] {
	if (entry && !entry.includes_all_room_types) {
		return entry.ota_room_types ?? []
	}
	const hotelMealPlan = hotel?.ota_meal_plans?.find(
		(otaMealPlan) => Number(otaMealPlan.id) === Number(mealPlan.id)
	)
	return hotelMealPlan?.ota_room_types ?? []
}

// Ordered grid rows: every active room type, each followed by the meal plans
// that room type is sold on.
function buildVisibleRows(
	activeRoomTypes: THotelOtaRoomType[],
	baseMealPlan: TOtaMealPlan | null,
	otherMealPlans: TOtaMealPlan[],
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	hotel?: Pick<IHotel, "ota_meal_plans">
): TGridVisibleRow[] {
	const rows: TGridVisibleRow[] = []
	activeRoomTypes.forEach((rt) => {
		if (baseMealPlan) {
			rows.push({ rt, mp: baseMealPlan, isBase: true })
		}
		otherMealPlans.forEach((mp) => {
			const mpEntry = mealPlans?.find(
				(entry) =>
					Number(entry?.ota_meal_plan?.id ?? entry?.ota_meal_plan) ===
					Number(mp.id)
			)
			// A plan the block restricts to specific rooms is only sold on those,
			// whatever the room type's own "takes every plan" flag says.
			const isRestrictedByBlock = Boolean(
				mpEntry && !mpEntry.includes_all_room_types
			)
			if (rt.includes_all_meal_plans && !isRestrictedByBlock) {
				rows.push({ rt, mp, isBase: false })
				return
			}
			const mpEffectiveRoomTypes = mealPlanRoomTypes(mp, mpEntry, hotel)

			// An empty list reads as "no restriction" rather than "no rooms" — the
			// room picker normalises an all-selected state to an empty array.
			if (
				mpEffectiveRoomTypes.length > 0 &&
				!mpEffectiveRoomTypes.some((ert) => Number(ert.id) === Number(rt.id))
			) {
				return
			}
			rows.push({ rt, mp, isBase: false })
		})
	})
	return rows
}

// Maps each row to the previous row for the same meal plan on a different room
// type — the "row above" a vertical rate comparison is made against.
export function buildAboveRowIndicesMap(
	visibleRows: TGridVisibleRow[]
): Record<number, number> {
	const map: Record<number, number> = {}
	visibleRows.forEach((row, idx) => {
		for (let i = idx - 1; i >= 0; i--) {
			if (
				visibleRows[i].mp.id === row.mp.id &&
				visibleRows[i].rt.id !== row.rt.id
			) {
				map[idx] = i
				break
			}
		}
	})
	return map
}

export function buildGridGeometry(
	hotel: IHotel,
	mealPlans: IRateBlockSchema["meal_plans"] | undefined,
	configureLowerOccupancy: boolean,
	overrides?: {
		roomTypes?: THotelOtaRoomType[]
		roomCols?: TRoomCol[]
		lowerCols?: TLowerCol[]
	}
): TGridGeometry {
	const activeRoomTypes = overrides?.roomTypes ?? hotel.ota_room_types ?? []
	const roomCols = overrides?.roomCols ?? buildRoomCols(hotel)
	const lowerCols = overrides?.lowerCols ?? buildLowerCols(activeRoomTypes)
	const { baseMealPlan, otherMealPlans } = splitMealPlans(mealPlans, hotel)
	const visibleRows = buildVisibleRows(
		activeRoomTypes,
		baseMealPlan,
		otherMealPlans,
		mealPlans,
		hotel
	)

	return {
		activeRoomTypes,
		baseMealPlan,
		otherMealPlans,
		lowerCols,
		numLowerCols: lowerCols.length,
		roomCols,
		visibleRows,
		itemsPerRoom:
			(configureLowerOccupancy ? lowerCols.length : 0) + 1 + roomCols.length,
		aboveRowIndicesMap: buildAboveRowIndicesMap(visibleRows),
		signature: gridGeometrySignature(
			{ visibleRows, lowerCols, roomCols },
			configureLowerOccupancy
		),
	}
}

// The rows and columns a block was actually priced with, read off the block
// itself rather than re-derived from the hotel.
//
// A block records its own pax types and rooms, and they are the authority: a
// sheet loaded for editing carries its hotels off the sheet response, which
// describe their room types but not the pax types the rate columns are built
// from. Falling back to `buildRoomCols(hotel)` there collapses the grid to its
// BASE column alone, so anything measured against it — the change count, the
// warning count — silently ignores every extra pax column.
//
// Absent pax types or rooms leave the override off entirely, which is what a
// create flow wants: the block has not been seeded yet and the hotel's own
// configuration is all there is to build from.
// A `baseline` widens the grid to what both blocks priced. A row or a lower
// occupancy column dropped from the block is still something the comparison has
// to say something about, and leaving it out of the geometry is what made a
// removal reportable only as text. Rows stay in the hotel's own order and lower
// columns in occupancy order, so anything restored lands back where it was
// rather than at the end.
//
// The extra pax columns are widened too: a hotel that re-cuts its child age
// ranges leaves the baseline priced in columns the block no longer has, and
// without them the comparison could not show what was dropped.
export function blockGridOverrides(
	hotel: IHotel,
	block: IRateBlockSchema | undefined,
	baseline?: IRateBlockSchema | undefined
): {
	roomTypes?: THotelOtaRoomType[]
	roomCols?: TRoomCol[]
	lowerCols?: TLowerCol[]
} {
	const paxTypes = block?.pax_types
	// An empty `rooms` means the block was never seeded rather than that every
	// room was removed — the same reading step 2 takes of it.
	const pricedRoomTypeIds =
		block?.rooms?.length || baseline?.rooms?.length
			? new Set(
					[...(block?.rooms || []), ...(baseline?.rooms || [])].map((room) =>
						Number(room.ota_room_type)
					)
				)
			: null
	return {
		roomTypes: pricedRoomTypeIds
			? (hotel.ota_room_types || []).filter((rt) =>
					pricedRoomTypeIds.has(Number(rt.id))
				)
			: undefined,
		roomCols: paxTypes?.length
			? mergeRoomCols(
					roomColsFromPaxTypes(paxTypes),
					roomColsFromPaxTypes(baseline?.pax_types)
				)
			: undefined,
		lowerCols:
			paxTypes?.length || baseline?.pax_types?.length
				? mergeLowerCols(
						lowerColsFromPaxTypes(paxTypes),
						lowerColsFromPaxTypes(baseline?.pax_types)
					)
				: undefined,
	}
}

export function blockGridOverridesFromSavedRooms(
	hotel: IHotel,
	block: IRateBlockSchema | undefined,
	baseline?: IRateBlockSchema | undefined
): {
	roomTypes?: THotelOtaRoomType[]
	roomCols?: TRoomCol[]
	lowerCols?: TLowerCol[]
} {
	const paxTypes = block?.pax_types
	const savedRoomById = new Map<number, IRateBlockSchema["rooms"][number]>()
	;(baseline?.rooms || []).forEach((room) =>
		savedRoomById.set(Number(room.ota_room_type), room)
	)
	;(block?.rooms || []).forEach((room) =>
		savedRoomById.set(Number(room.ota_room_type), room)
	)

	return {
		roomTypes:
			savedRoomById.size > 0
				? (hotel.ota_room_types || []).flatMap((rt) => {
						const room = savedRoomById.get(Number(rt.id))
						if (!room) return []
						return [
							{
								...rt,
								pax_types: [],
								base_adult_occupancy: room.base_adult_occupancy,
								base_child_occupancy: room.base_child_occupancy,
								max_occupancy: room.max_occupancy,
								max_adult_occupancy: room.max_adult_occupancy,
								max_child_occupancy: room.max_child_occupancy,
							},
						]
					})
				: undefined,
		roomCols: paxTypes?.length
			? mergeRoomCols(
					roomColsFromPaxTypes(paxTypes),
					roomColsFromPaxTypes(baseline?.pax_types)
				)
			: undefined,
		lowerCols:
			paxTypes?.length || baseline?.pax_types?.length
				? mergeLowerCols(
						lowerColsFromPaxTypes(paxTypes),
						lowerColsFromPaxTypes(baseline?.pax_types)
					)
				: undefined,
	}
}

// The extra pax columns of two blocks. The current block keeps its own order,
// and a column only the baseline priced goes right after the last current
// column of its pax type, so a re-cut CWEB range stays beside its siblings.
function mergeRoomCols(current: TRoomCol[], baseline: TRoomCol[]): TRoomCol[] {
	const merged = [...current]
	const currentKeys = new Set(current.map((col) => col.key))
	baseline.forEach((col) => {
		if (currentKeys.has(col.key)) return
		let lastSibling = -1
		merged.forEach((other, idx) => {
			if (other.paxType === col.paxType) lastSibling = idx
		})
		merged.splice(lastSibling === -1 ? merged.length : lastSibling + 1, 0, col)
	})
	return merged
}

// The extra pax columns a block gained or lost against its baseline. A side
// with no pax types was never priced, so it has nothing to be compared on.
export type TRoomColChanges = { added: TRoomCol[]; removed: TRoomCol[] }

export function roomColChanges(
	block: IRateBlockSchema | undefined,
	baseline: IRateBlockSchema | undefined | null
): TRoomColChanges {
	if (!block?.pax_types?.length || !baseline?.pax_types?.length) {
		return { added: [], removed: [] }
	}
	const currentCols = roomColsFromPaxTypes(block.pax_types)
	const baselineCols = roomColsFromPaxTypes(baseline.pax_types)
	const currentKeys = new Set(currentCols.map((col) => col.key))
	const baselineKeys = new Set(baselineCols.map((col) => col.key))
	return {
		added: currentCols.filter((col) => !baselineKeys.has(col.key)),
		removed: baselineCols.filter((col) => !currentKeys.has(col.key)),
	}
}

// Whether two pax type lists price different extra columns. A list with no
// pax types was never priced, so it has nothing to differ from.
export function paxColumnsChanged(
	paxTypes: IRateBlockSchema["pax_types"] | undefined,
	baseline: IRateBlockSchema["pax_types"] | undefined
): boolean {
	if (!paxTypes?.length || !baseline?.length) return false
	const keys = new Set(roomColsFromPaxTypes(paxTypes).map((col) => col.key))
	const baselineKeys = roomColsFromPaxTypes(baseline).map((col) => col.key)
	return (
		keys.size !== baselineKeys.length ||
		baselineKeys.some((key) => !keys.has(key))
	)
}

// A saved block re-cut to the extra pax columns its hotel prices today, for the
// blocks the user never opens. The grid does this itself when it mounts, but a
// block that is never mounted would otherwise carry its saved columns into the
// sheet summary, the preview and the payload.
//
// Returns `block` itself when there is nothing to reconcile — never priced, a
// hotel record without room types (not the detailed one), or columns that
// already match — so callers can tell a moved block from an untouched one. Rates
// the user typed are kept as they are, a column the block already had keeps the
// derivation it was saved with, and a new column starts from the default.
export function reconcileBlockColumns(
	block: IRateBlockSchema,
	hotel: IHotel
): IRateBlockSchema {
	if (!block.pax_types?.length || !hotel.ota_room_types?.length) return block

	const roomCols = buildRoomCols(hotel)
	const savedKeys = new Set(
		roomColsFromPaxTypes(block.pax_types).map((col) => col.key)
	)
	if (
		savedKeys.size === roomCols.length &&
		roomCols.every((col) => savedKeys.has(col.key))
	) {
		return block
	}

	const configureLowerOccupancy = Boolean(
		block.is_lower_occupancy_rate_configured
	)
	const geometry = buildGridGeometry(
		hotel,
		block.meal_plans,
		configureLowerOccupancy,
		{ roomTypes: blockGridOverrides(hotel, block).roomTypes, roomCols }
	)

	const keysWithSavedDerivation = occupancyKeysOf(block.pax_types)
	const defaults = getInitialColumnDerivations(roomCols)
	const saved = columnDerivationsFromPaxTypes(block.pax_types, roomCols)
	const columnDerivations: Record<string, TColumnDerivation> = {}
	roomCols.forEach((col) => {
		columnDerivations[col.key] = keysWithSavedDerivation.has(col.key)
			? saved[col.key]
			: defaults[col.key]
	})

	const rates = deriveGridRates({
		rates: buildGridRates(
			geometry.visibleRows,
			geometry.lowerCols,
			roomCols,
			configureLowerOccupancy,
			block.rates
		),
		geometry,
		configureLowerOccupancy,
		columnDerivations,
		childRateSameAsAdult: block.child_meal_rate_same_as_adult,
		mealRates: syncBlockMealRates(block.meal_plans, block.meal_rates),
		roundingValue: block.rounding_point?.id ?? null,
	})

	return {
		...block,
		pax_types: buildPaxTypes(
			configureLowerOccupancy ? geometry.lowerCols : [],
			roomCols,
			columnDerivations
		),
		rates: toBlockRates(rates),
	}
}

// The lower occupancy columns of two blocks, in occupancy order. Turning the
// switch off strips them from the block's pax types altogether, so the side
// that still has them is the only record of what the grid used to look like.
function mergeLowerCols(
	current: TLowerCol[],
	baseline: TLowerCol[]
): TLowerCol[] {
	const byOcc = new Map<number, TLowerCol>()
	// The current block wins on a collision — same column, but its own labelling
	baseline.forEach((col) => byOcc.set(col.occ, col))
	current.forEach((col) => byOcc.set(col.occ, col))
	return Array.from(byOcc.values()).sort((a, b) => a.occ - b.occ)
}

// `buildGridGeometry` for a block that is not being edited — the headless
// summaries and the read only preview table. Every caller goes through this so
// the grid a change is counted against cannot differ from the grid it is shown
// in. `baseline` is passed only where a comparison is being drawn or counted —
// the warnings describe the sheet as it stands, and a removed room's empty
// cells are not something to warn about.
export function buildBlockGridGeometry(
	hotel: IHotel,
	block: IRateBlockSchema | undefined,
	configureLowerOccupancy: boolean,
	baseline?: IRateBlockSchema | undefined
): TGridGeometry {
	return buildGridGeometry(
		hotel,
		block?.meal_plans,
		configureLowerOccupancy,
		blockGridOverrides(hotel, block, baseline)
	)
}

// ---------------------------------------------------------------------------
// Rate hierarchy
//
// What a cell may be compared against horizontally. Columns render in
// `buildRoomCols` order — whatever order the backend returns its pax types in —
// so a cell's left hand neighbour is not the column it should be measured
// against: CWEB routinely lands right of CNB and would be flagged for costing
// more, which is exactly what it should cost. Comparisons follow the rank below
// rather than the layout.
// ---------------------------------------------------------------------------

// Extra pax types, most expensive first
const PAX_TYPE_RANK: Record<string, number> = { AWEB: 0, CWEB: 1, CNB: 2 }

// The column offset of BASE within a room's row
function baseColOffsetOf(
	numLowerCols: number,
	configureLowerOccupancy: boolean
): number {
	return configureLowerOccupancy ? numLowerCols : 0
}

// One horizontal comparison: the rate at `offset` must not exceed the rate at
// any of `upperBounds`, nearest first. Offsets are within a room's row, so a
// cell index is `rowIdx * itemsPerRoom + offset`. A column is checked against
// every column ranked above it, not only its immediate neighbour — a chain of
// single neighbour-only checks lets a middle link mask a cell two or more
// links deeper, and lets one blank cell disable the only check a column had.
export type TRateComparison = {
	offset: number
	label: string
	upperBounds: { offset: number; label: string }[]
}

// Orders the extra pax columns by what they should cost, most expensive first:
// AWEB, then CWEB, then CNB, and within one pax type the older age band before
// the younger one. A column carrying no age range sorts first within its type.
function rankExtraCols(roomCols: TRoomCol[]): number[] {
	return roomCols
		.map((col, index) => ({ col, index }))
		.sort((a, b) => {
			const typeDiff =
				(PAX_TYPE_RANK[a.col.paxType] ?? 99) -
				(PAX_TYPE_RANK[b.col.paxType] ?? 99)
			if (typeDiff !== 0) return typeDiff
			const ageA = a.col.startAge ?? a.col.endAge
			const ageB = b.col.startAge ?? b.col.endAge
			if (ageA === null && ageB === null) return a.index - b.index
			if (ageA === null) return -1
			if (ageB === null) return 1
			if (ageA !== ageB) return ageB - ageA
			return a.index - b.index
		})
		.map(({ index }) => index)
}

// The horizontal comparisons that apply to a room sold at `roomBaseOccupancy`.
// Two chains, both pointing at the column that must be the more expensive one:
//
//   lower occupancies  A1 ≤ A2 ≤ … ≤ BASE   (by adult count)
//   extra pax          BASE ≥ AWEB ≥ CWEB ≥ CNB   (by the rank above)
//
// BASE itself sits at the top of both and is never compared. A room cannot be
// sold below its own base occupancy, so the lower occupancy chain skips the
// columns at or above it — the same cells `buildGridRates` marks `enabled: 0`.
export function buildRateComparisons(
	{ lowerCols, roomCols }: Pick<TGridGeometry, "lowerCols" | "roomCols">,
	configureLowerOccupancy: boolean,
	roomBaseOccupancy: number
): TRateComparison[] {
	const comparisons: TRateComparison[] = []
	const numLowerCols = lowerCols.length
	const baseOffset = baseColOffsetOf(numLowerCols, configureLowerOccupancy)
	const BASE_LABEL = "Base"

	if (configureLowerOccupancy) {
		lowerCols.forEach((col, colIdx) => {
			if (col.occ >= roomBaseOccupancy) return
			// Every column sold at a higher occupancy than this one, nearest first,
			// then BASE — not only the next column in the list.
			const upperBounds = lowerCols
				.slice(colIdx + 1)
				.filter((c) => c.occ < roomBaseOccupancy)
				.map((c, i) => ({ offset: colIdx + 1 + i, label: c.label }))
			upperBounds.push({ offset: baseOffset, label: BASE_LABEL })
			comparisons.push({ offset: colIdx, label: col.label, upperBounds })
		})
	}

	const rankedOffsets = rankExtraCols(roomCols).map((colIndex) => ({
		offset: baseOffset + 1 + colIndex,
		label: roomCols[colIndex].label || roomCols[colIndex].key.toUpperCase(),
	}))
	rankedOffsets.forEach(({ offset, label }, rankIdx) => {
		// Every column ranked above this one, nearest first, then BASE.
		const upperBounds = rankedOffsets
			.slice(0, rankIdx)
			.reverse()
			.concat([{ offset: baseOffset, label: BASE_LABEL }])
		comparisons.push({ offset, label, upperBounds })
	})

	return comparisons
}

// ---------------------------------------------------------------------------
// Block summary — changes, warnings and errors for one hotel x season block
//
// Every function here takes plain data and returns plain data, so a block that
// is not mounted can be summarised just as well as the active one. The whole
// sheet rollup below and the per-flush update in Step 2 both go through
// `computeBlockSummary`, so the header counts and the in-grid pills cannot
// drift apart.
// ---------------------------------------------------------------------------

type TIssueSource = "roomRates" | "mealRates"

// The part of a rate block a summary entry points at, for "View" to scroll to
export type TBlockSection = TIssueSource | "mealPlans"

type TBlockChange = { message: string; section?: TBlockSection }

type TBlockSummary = {
	changes: TBlockChange[]
	warnings: Record<TIssueSource, string[]>
	errors: Record<TIssueSource, string[]>
}

// A rate cell holds a number, an empty string or null depending on whether it
// has been typed into, so the three have to collapse before two of them can be
// compared. An unpriced cell reads as null rather than 0 — `Number("")` is 0,
// which would make a blank cell and a zero rate look alike.
export function toNum(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null
	const num = Number(value)
	return isNaN(num) ? null : num
}

function isDifferent(a: number | null, b: number | null): boolean {
	if (a === null && b === null) return false
	if (a === null || b === null) return true
	return a !== b
}

function titleCase(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1)
}

export function pluralize(count: number, singular: string) {
	return `${count} ${count === 1 ? singular : `${singular}s`}`
}

// Human readable name for one cell of the rate grid, e.g "Deluxe - CP (Double)"
function rateCellLabel(
	rate: Pick<TGridRate, "ota_room_type" | "ota_meal_plan" | "occupancy">,
	hotel: IHotel,
	roomTypes: THotelOtaRoomType[],
	roomCols: TRoomCol[]
): string {
	const roomType = roomTypes.find(
		(rt) => Number(rt.id) === Number(rate.ota_room_type)
	)
	const mealPlan = hotel.ota_meal_plans?.find(
		(mp) => Number(mp.id) === Number(rate.ota_meal_plan)
	)

	let occLabel = rate.occupancy || ""
	if (occLabel === "base") {
		occLabel = getOccupancyInfo(
			roomType ? getRoomBaseOccupancy(roomType) : 2
		).label
	} else {
		const col = roomCols.find((c) => c.key === occLabel)
		if (col) {
			occLabel = col.label || col.key.toUpperCase()
		} else {
			const occCount = getOccupancyCount(occLabel)
			if (occCount) occLabel = getOccupancyInfo(occCount).label
		}
	}

	const roomName = roomType?.name || "Room"
	return mealPlan?.name
		? `${roomName} - ${mealPlan.name} (${occLabel})`
		: `${roomName} (${occLabel})`
}

// Meal course hierarchy check — breakfast should cost less than lunch, which
// should cost less than dinner.
export function getHierarchyWarnings<T>(
	courses: TMealCourse[],
	rates: T[] | undefined,
	getRate: (item: T) => number | string | null | undefined,
	getCourseName?: (item: T) => string | null | undefined
): Record<number, string> {
	const warnings: Record<number, string> = {}
	let maxVal: number | null = null
	let maxName = ""

	const byCourse = new Map<string, T>()
	if (getCourseName && rates) {
		for (const rate of rates) {
			const name = rate ? getCourseName(rate) : null
			if (name) byCourse.set(name.toLowerCase(), rate)
		}
	}

	courses.forEach((course, idx) => {
		// A custom plan has no place in the breakfast < lunch < dinner ordering,
		// whatever it happens to be called
		if (
			course.ota_meal_plan != null ||
			!MEAL_TO_MEAL_TYPES.AP.includes(course.name.toLowerCase())
		) {
			return
		}
		const matched = byCourse.get(course.name.toLowerCase()) ?? rates?.[idx]
		const val = matched ? toNum(getRate(matched)) : null

		if (val != null) {
			if (maxVal != null && val <= maxVal) {
				warnings[idx] =
					`${course.name[0].toUpperCase()}${course.name.slice(1)} rate should be greater than ${maxName} rate`
			} else {
				maxVal = val
				maxName = course.name[0].toUpperCase() + course.name.slice(1)
			}
		}
	})

	return warnings
}

// Full error lists per source. `computeBlockErrors` below narrows this to the
// first message per source for the submit gate and the inline banners.
export function computeBlockErrorMessages(
	block: TRateBlockPricing | undefined,
	hotel: IHotel
): Record<TIssueSource, string[]> {
	const roomRates: string[] = []
	const mealRates: string[] = []

	const roomTypes = hotel.ota_room_types?.length
		? hotel.ota_room_types
		: ((hotel.room_types || []) as unknown as THotelOtaRoomType[])
	const roomCols = buildRoomCols(hotel)

	// Room rates. A block that was never opened holds no rates at all, which is
	// reported once rather than once per cell so the header count stays legible.
	const rates = block?.rates || []
	if (rates.length === 0) {
		if ((hotel.room_types || []).length > 0) {
			roomRates.push("Room rates are not set.")
		}
	} else {
		const derivedOccupancies = derivedOccupanciesFromPaxTypes(block?.pax_types)
		const baseMealPlan = splitMealPlans(block?.meal_plans, hotel).baseMealPlan
		const derivation = {
			derivedOccupancies,
			baseMealPlanId: baseMealPlan ? Number(baseMealPlan.id) : null,
		}
		for (const rate of rates) {
			// A blank derived cell is not a missing entry, its source reports that.
			// One holding a value is checked like any other: a formula can drive it
			// below zero, and the grid flags it as such.
			if (isDerivedRate(rate, derivation) && !isNumeric(rate.rate_before_tax))
				continue
			const error = validateRate(
				rate.rate_before_tax,
				"Rate must be a non-negative number."
			)
			if (error) {
				roomRates.push(
					`${rateCellLabel(rate, hotel, roomTypes, roomCols)}: ${error}`
				)
			}
		}
	}

	// Meal rates, same treatment: nothing set at all is one message naming the
	// courses, a partially filled block is one message per missing rate. A block
	// priced on its base plan alone has no meal rates to miss.
	const mealRateValues = block?.meal_rates || []
	if (!blockNeedsMealRates(block?.meal_plans, hotel)) {
		return { roomRates, mealRates }
	}
	if (mealRateValues.length === 0) {
		const expected = getExpectedMealCourses(block?.meal_plans, hotel)
		if (expected.length > 0) {
			mealRates.push(
				`Meal rates are not set for ${expected
					.map((course) => titleCase(course.name))
					.join(", ")}.`
			)
		}
	} else {
		const courses = getExpectedMealCourses(block?.meal_plans, hotel)
		mealRateValues.forEach((mealRate, idx) => {
			const name = titleCase(
				mealRateCourseName(mealRate, courses, idx) || "Meal"
			)
			const adultError = validateRate(
				mealRate?.per_adult_rate_before_tax,
				"Adult rate must be a non-negative number."
			)
			if (adultError) {
				mealRates.push(`${name}: ${adultError}`)
			}
			if (!block?.child_meal_rate_same_as_adult) {
				const childError = validateRate(
					mealRate?.per_child_rate_before_tax,
					"Child rate must be a non-negative number, or mark it as same as adult."
				)
				if (childError) {
					mealRates.push(`${name}: ${childError}`)
				}
			}
		})
	}

	return { roomRates, mealRates }
}

// Non-blocking checks: rates that are zero, or that break the expected ordering
// between occupancies, room types and meal courses.
function computeBlockWarnings(
	block: IRateBlockSchema | undefined,
	hotel: IHotel
): Record<TIssueSource, string[]> {
	const roomRates: string[] = []
	const mealRates: string[] = []
	if (!block) return { roomRates, mealRates }

	// --- Meal rates -------------------------------------------------------
	// Skipped entirely for a block priced on its base plan alone — it carries no
	// meal rates to order against each other.
	if (blockNeedsMealRates(block.meal_plans, hotel)) {
		const courses = getExpectedMealCourses(block.meal_plans, hotel)
		const mealRateValues = block.meal_rates || []
		const sameAsAdult = Boolean(block.child_meal_rate_same_as_adult)

		const adultWarnings = getHierarchyWarnings(
			courses,
			mealRateValues,
			(r) => r?.per_adult_rate_before_tax,
			(r) => r?.meal_type
		)
		Object.values(adultWarnings).forEach((message) =>
			mealRates.push(`Adult ${message}`)
		)

		if (!sameAsAdult) {
			const childWarnings = getHierarchyWarnings(
				courses,
				mealRateValues,
				(r) => r?.per_child_rate_before_tax,
				(r) => r?.meal_type
			)
			Object.values(childWarnings).forEach((message) =>
				mealRates.push(`Child ${message}`)
			)

			courses.forEach((course, idx) => {
				const adult = toNum(mealRateValues[idx]?.per_adult_rate_before_tax)
				const child = toNum(mealRateValues[idx]?.per_child_rate_before_tax)
				if (adult != null && child != null && child > adult) {
					mealRates.push(
						`${titleCase(course.name)} child rate is more than adult rate`
					)
				}
			})
		}
	}

	// --- Room rates -------------------------------------------------------
	const configureLowerOccupancy = Boolean(
		block.is_lower_occupancy_rate_configured
	)
	const geometry = buildBlockGridGeometry(hotel, block, configureLowerOccupancy)

	if (!geometry.visibleRows.length) return { roomRates, mealRates }

	const grid = buildGridRates(
		geometry.visibleRows,
		geometry.lowerCols,
		geometry.roomCols,
		configureLowerOccupancy,
		block.rates
	)

	roomRates.push(
		...collectRoomRateWarnings(geometry, configureLowerOccupancy, (idx) =>
			toNum(grid[idx]?.rate_before_tax)
		)
	)

	return { roomRates, mealRates }
}

// Every room rate warning in the grid, for one set of values. The live grid
// reads its cells out of the form and an unmounted block reads them out of
// `buildGridRates`, so the values arrive through `getRate` and the walk itself
// is shared — the in-grid pills and the Step 2 counts cannot drift apart.
//
// Each row is a room type x meal plan, so the messages name both: the same room
// appears once per plan, and a message naming only the room repeats identically
// across those rows with no way to tell them apart.
export function collectRoomRateWarnings(
	geometry: TRoomRateWarningGeometry,
	configureLowerOccupancy: boolean,
	getRate: (cellIdx: number) => number | null
): string[] {
	return collectRoomRateWarningEntries(
		geometry,
		configureLowerOccupancy,
		getRate
	).map((warning) => warning.message)
}

// Why a cell is flagged. The grid shows one icon per cell, so a cell carrying
// more than one warning has to pick — see `collectRoomRateCellWarnings`.
type TRoomRateWarningKind = "zero" | "lower_than_above" | "higher_than_upper"

type TRoomRateWarning = {
	cellIdx: number
	kind: TRoomRateWarningKind
	message: string
}

export type TRoomRateWarningGeometry = Pick<
	TGridGeometry,
	| "lowerCols"
	| "roomCols"
	| "visibleRows"
	| "itemsPerRoom"
	| "aboveRowIndicesMap"
>

// The warning walk itself, carrying the cell each message belongs to. The prose
// list above and the per cell icons in the grid are both built from this, so a
// cell cannot be flagged in one and left clean in the other.
function collectRoomRateWarningEntries(
	{
		lowerCols,
		roomCols,
		visibleRows,
		itemsPerRoom,
		aboveRowIndicesMap,
	}: TRoomRateWarningGeometry,
	configureLowerOccupancy: boolean,
	getRate: (cellIdx: number) => number | null
): TRoomRateWarning[] {
	const warnings: TRoomRateWarning[] = []
	const baseOffset = baseColOffsetOf(lowerCols.length, configureLowerOccupancy)

	// One per distinct base occupancy rather than per row — the lower occupancy
	// chain is the only part that varies, and a hotel has a handful of them.
	const comparisonsByBaseOcc = new Map<number, TRateComparison[]>()
	const comparisonsFor = (baseOcc: number) => {
		let comparisons = comparisonsByBaseOcc.get(baseOcc)
		if (!comparisons) {
			comparisons = buildRateComparisons(
				{ lowerCols, roomCols },
				configureLowerOccupancy,
				baseOcc
			)
			comparisonsByBaseOcc.set(baseOcc, comparisons)
		}
		return comparisons
	}

	visibleRows.forEach((row, rIdx) => {
		const { rt, mp } = row
		const rtBaseOcc = getRoomBaseOccupancy(rt)
		const rowStart = rIdx * itemsPerRoom
		const aboveRowIdx = aboveRowIndicesMap[rIdx]
		const aboveRow =
			aboveRowIdx !== undefined ? visibleRows[aboveRowIdx] : undefined

		// Label of the column at an offset, as the messages name it
		const labelAt = (offset: number): string => {
			if (offset === baseOffset) return "base"
			if (offset < baseOffset) return lowerCols[offset].label
			const col = roomCols[offset - baseOffset - 1]
			return col.label || col.key.toUpperCase()
		}

		// The offsets this row actually prices — a room cannot be sold below its
		// own base occupancy, and those cells are placeholders.
		const offsets: number[] = []
		if (configureLowerOccupancy) {
			lowerCols.forEach((col, colIdx) => {
				if (col.occ < rtBaseOcc) offsets.push(colIdx)
			})
		}
		offsets.push(baseOffset)
		roomCols.forEach((_col, colIndex) => {
			offsets.push(baseOffset + 1 + colIndex)
		})

		offsets.forEach((offset) => {
			const cellIdx = rowStart + offset
			const currNum = getRate(cellIdx)
			const label = labelAt(offset)

			if (currNum === 0) {
				warnings.push({
					cellIdx,
					kind: "zero",
					message: `${rt.name} × ${mp.name} ${label} rate is 0`,
				})
			}

			// Vertical: the same column on the previous room type's row for this
			// meal plan, which is expected to be the cheaper one.
			if (aboveRow && currNum !== null) {
				const vertNum = getRate(aboveRowIdx * itemsPerRoom + offset)
				if (vertNum !== null && vertNum > currNum) {
					warnings.push({
						cellIdx,
						kind: "lower_than_above",
						message: `${rt.name} × ${mp.name} → ${label} rate is less than ${aboveRow.rt.name} × ${aboveRow.mp.name} ${label} rate`,
					})
				}
			}
		})

		// Horizontal: each column against every column that must be the more
		// expensive one, which is the rate hierarchy rather than the column to its
		// left — checked nearest first so a blank or already-wrong neighbour does
		// not hide a violation further up the chain.
		comparisonsFor(rtBaseOcc).forEach(({ offset, label, upperBounds }) => {
			const cellIdx = rowStart + offset
			const currNum = getRate(cellIdx)
			if (currNum === null) return
			for (const upperBound of upperBounds) {
				const upperNum = getRate(rowStart + upperBound.offset)
				if (upperNum !== null && currNum > upperNum) {
					warnings.push({
						cellIdx,
						kind: "higher_than_upper",
						message: `${rt.name} × ${mp.name} → ${label} rate is more than ${upperBound.label} rate`,
					})
					break
				}
			}
		})
	})

	return warnings
}

// Which warning wins when a cell carries more than one. Mirrors the precedence
// the rate input applies in step 2, so the preview flags a cell for the same
// reason the editor does.
const ROOM_RATE_WARNING_RANK: Record<TRoomRateWarningKind, number> = {
	lower_than_above: 0,
	higher_than_upper: 1,
	zero: 2,
}

// The same warnings, addressed by cell, for anything rendering one icon per
// cell. A cell absent from the map is clean.
export function collectRoomRateCellWarnings(
	geometry: TRoomRateWarningGeometry,
	configureLowerOccupancy: boolean,
	getRate: (cellIdx: number) => number | null
): Map<number, string> {
	const byCell = new Map<number, TRoomRateWarning>()
	collectRoomRateWarningEntries(
		geometry,
		configureLowerOccupancy,
		getRate
	).forEach((warning) => {
		const existing = byCell.get(warning.cellIdx)
		if (
			!existing ||
			ROOM_RATE_WARNING_RANK[warning.kind] <
				ROOM_RATE_WARNING_RANK[existing.kind]
		) {
			byCell.set(warning.cellIdx, warning)
		}
	})
	return new Map(
		Array.from(byCell, ([cellIdx, warning]) => [cellIdx, warning.message])
	)
}

// ---------------------------------------------------------------------------
// Rate comparison — one grid against the baseline it was loaded with
// ---------------------------------------------------------------------------

// How a cell moved against its baseline. A cell that gained or lost a rate
// outright has no direction to report, so it is neither of the two.
export type TRateCellChangeStatus =
	| "increased"
	| "decreased"
	| "added"
	| "removed"

export type TRateCellDiff = {
	current: number | null
	initial: number | null
	status: TRateCellChangeStatus
}

// Diffs two grids built off the same geometry, which is what makes them
// index-aligned — see `buildGridRates`. Only cells that actually moved are in
// the map, so a lookup miss reads as unchanged.
//
// A block with no priced baseline cell has never been saved, and diffing
// against it would paint every cell as added — that is not a change the user
// made, so it reports nothing at all.
export function buildRateCellDiffs(
	currentGrid: TGridRate[] | undefined,
	baselineGrid: TGridRate[] | undefined,
	allowEmptyBaseline = false
): Map<number, TRateCellDiff> {
	const diffs = new Map<number, TRateCellDiff>()
	if (!currentGrid?.length || !baselineGrid?.length) return diffs

	if (!allowEmptyBaseline) {
		const hasBaseline = baselineGrid.some(
			(rate) => rate && rate.rate_before_tax != null
		)
		if (!hasBaseline) return diffs
	}

	currentGrid.forEach((rate, cellIdx) => {
		// Placeholders for occupancies a room cannot take never render a value
		if (!rate || rate.enabled === 0 || rate.enabled === false) return

		const current = toNum(rate.rate_before_tax)
		const initial = toNum(baselineGrid[cellIdx]?.rate_before_tax)

		if (current === initial) return
		if (initial === null) {
			diffs.set(cellIdx, { current, initial, status: "added" })
		} else if (current === null) {
			diffs.set(cellIdx, { current, initial, status: "removed" })
		} else {
			diffs.set(cellIdx, {
				current,
				initial,
				status: current > initial ? "increased" : "decreased",
			})
		}
	})

	return diffs
}

// One line describing how a policy moved. A sheet pins a policy *version*, so
// two different ids can still be the same policy — that reads as an update to a
// newer version rather than as a switch to a different policy.
export function describePolicyChange(
	label: string,
	previous: TAnyPolicy | null | undefined,
	current: TAnyPolicy | null | undefined
): string | null {
	const previousId = previous?.id ?? null
	const currentId = current?.id ?? null
	if (previousId === currentId) return null
	if (
		previous &&
		current &&
		policyHeadId(previous) != null &&
		policyHeadId(previous) === policyHeadId(current)
	) {
		return `${label} updated: ${policyHeadName(previous) || "None"} v${
			previous.version
		} → v${current.version}`
	}
	return `${label} changed: ${policyHeadName(previous) || "None"} → ${
		policyHeadName(current) || "None"
	}`
}

// What the user has altered in this block. In edit mode that means everything
// that differs from the version loaded from the server; in create mode there is
// no such baseline, and the only thing that can be "changed" is a block that
// overrode one of the policies picked for the hotel in step 1.
function computeBlockChanges(
	block: IRateBlockSchema | undefined,
	baseline: IRateBlockSchema | undefined,
	hotel: IHotel,
	hotelPolicy: TCancellationPolicy | null,
	hotelPaymentPolicy: TPaymentPolicy | null
): TBlockChange[] {
	const changes: TBlockChange[] = []
	const add = (message: string, section?: TBlockSection) => {
		changes.push({ message, section })
	}
	if (!block) return changes

	// An override is what the block says it is, not what an id comparison
	// suggests: the two agree only until the hotel is moved onto the same policy
	// the block had already overridden to. In edit mode an override the sheet was
	// saved with is not a change — only newly taking one, or dropping one, is.
	const overrides = [
		{
			label: "Cancellation policy",
			isManual: Boolean(block.cancellation_policy_is_manual),
			wasManual: Boolean(baseline?.cancellation_policy_is_manual),
			hotelPolicy: hotelPolicy as TAnyPolicy | null,
			blockPolicy: block.cancellation_policy as TAnyPolicy | null,
			baselinePolicy: baseline?.cancellation_policy as TAnyPolicy | null,
		},
		{
			label: "Payment policy",
			isManual: Boolean(block.payment_policy_is_manual),
			wasManual: Boolean(baseline?.payment_policy_is_manual),
			hotelPolicy: hotelPaymentPolicy as TAnyPolicy | null,
			blockPolicy: block.payment_policy as TAnyPolicy | null,
			baselinePolicy: baseline?.payment_policy as TAnyPolicy | null,
		},
	]
	overrides.forEach(
		({
			label,
			isManual,
			wasManual,
			hotelPolicy: hp,
			blockPolicy,
			baselinePolicy,
		}) => {
			if (isManual && (!baseline || !wasManual)) {
				add(
					`${label} overridden: ${policyHeadName(hp) || "None"} → ${
						policyHeadName(blockPolicy) || "None"
					}`
				)
			} else if (baseline && wasManual && !isManual) {
				add(
					`${label} override removed: back to ${policyHeadName(hp) || "None"}`
				)
			} else if (baseline && isManual && wasManual) {
				// Both sides already had their own override — a plain id/version
				// diff here is a genuine change to the season's own override, not
				// fan-out from a hotel-level edit (which is reported once at hotel
				// scope), so it is the only case worth diffing here.
				const change = describePolicyChange(label, baselinePolicy, blockPolicy)
				if (change) add(change)
			}
		}
	)

	if (!baseline) return changes

	if (block.rounding_point?.id !== baseline.rounding_point?.id) {
		add(
			`Rounding changed: ${baseline.rounding_point?.name || "None"} → ${
				block.rounding_point?.name || "None"
			}`,
			"roomRates"
		)
	}

	if (block.pricing_type?.id !== baseline.pricing_type?.id) {
		add(
			`Pricing type changed: ${baseline.pricing_type?.name || "None"} → ${
				block.pricing_type?.name || "None"
			}`,
			"roomRates"
		)
	}

	if (
		Boolean(block.child_meal_rate_same_as_adult) !==
		Boolean(baseline.child_meal_rate_same_as_adult)
	) {
		add(
			block.child_meal_rate_same_as_adult
				? "Child meal rate is now same as adult"
				: "Child meal rate is no longer same as adult",
			"mealRates"
		)
	}

	if (
		Boolean(block.is_lower_occupancy_rate_configured) !==
		Boolean(baseline.is_lower_occupancy_rate_configured)
	) {
		add(
			block.is_lower_occupancy_rate_configured
				? "Lower occupancy rates enabled"
				: "Lower occupancy rates disabled",
			"roomRates"
		)
	}

	// --- Room types -------------------------------------------------------
	const roomTypeName = (id: number) =>
		hotel.ota_room_types?.find((rt) => Number(rt.id) === id)?.name ||
		`Room ${id}`

	if (baseline.rooms && baseline.rooms.length > 0) {
		const currentRoomIds = new Set(
			(block.rooms || []).map((r) => Number(r.ota_room_type))
		)
		const baselineRoomIds = new Set(
			(baseline.rooms || []).map((r) => Number(r.ota_room_type))
		)

		currentRoomIds.forEach((id) => {
			if (!baselineRoomIds.has(id)) {
				add(`Room ${roomTypeName(id)} added`, "roomRates")
			}
		})
		baselineRoomIds.forEach((id) => {
			if (!currentRoomIds.has(id)) {
				add(`Room ${roomTypeName(id)} removed`, "roomRates")
			}
		})
	}

	// --- Pax types --------------------------------------------------------
	const paxColChanges = roomColChanges(block, baseline)
	paxColChanges.added.forEach((col) => {
		add(`Pax type ${col.label || col.key} added`, "roomRates")
	})
	paxColChanges.removed.forEach((col) => {
		add(`Pax type ${col.label || col.key} removed`, "roomRates")
	})

	// --- Meal plans -------------------------------------------------------
	const mealPlanName = (mp: IRateBlockSchema["meal_plans"][number]) =>
		mp.ota_meal_plan?.name || "Meal plan"
	const currentPlans = new Map(
		(block.meal_plans || []).map((mp) => [Number(mp.ota_meal_plan?.id), mp])
	)
	const baselinePlans = new Map(
		(baseline.meal_plans || []).map((mp) => [Number(mp.ota_meal_plan?.id), mp])
	)
	currentPlans.forEach((mp, id) => {
		if (!baselinePlans.has(id)) {
			add(`Meal plan ${mealPlanName(mp)} added`, "mealPlans")
			return
		}
		const before = baselinePlans.get(id)
		if (!before) return
		if (Boolean(before.is_base) !== Boolean(mp.is_base)) {
			add(
				mp.is_base
					? `Meal plan ${mealPlanName(mp)} is now the base plan`
					: `Meal plan ${mealPlanName(mp)} is no longer the base plan`,
				"mealPlans"
			)
		}
		const roomsBefore = before.includes_all_room_types
			? "all room types"
			: `${(before.ota_room_types || []).length} room types`
		const roomsAfter = mp.includes_all_room_types
			? "all room types"
			: `${(mp.ota_room_types || []).length} room types`
		if (roomsBefore !== roomsAfter) {
			add(
				`Meal plan ${mealPlanName(mp)} applies to ${roomsBefore} → ${roomsAfter}`,
				"mealPlans"
			)
		}
	})
	baselinePlans.forEach((mp, id) => {
		if (!currentPlans.has(id)) {
			add(`Meal plan ${mealPlanName(mp)} removed`, "mealPlans")
		}
	})

	// --- Meal rates -------------------------------------------------------
	const courses = getExpectedMealCourses(block.meal_plans, hotel)
	const currentMealRates = block.meal_rates || []
	const baselineMealRates = baseline.meal_rates || []
	const baselineByCourse = new Map<string, (typeof baselineMealRates)[number]>()
	baselineMealRates.forEach((mr) => {
		const key = mealRateKey(mr)
		if (key) baselineByCourse.set(key, mr)
	})
	currentMealRates.forEach((mealRate, idx) => {
		const courseName = mealRateCourseName(mealRate, courses, idx)
		if (!courseName) return
		const courseKey =
			mealRateKey(mealRate) ??
			(courses[idx] ? mealCourseKey(courses[idx]) : null)
		const before =
			(courseKey ? baselineByCourse.get(courseKey) : undefined) ??
			baselineMealRates[idx]
		const name = titleCase(String(courseName))

		const adultNow = toNum(mealRate?.per_adult_rate_before_tax)
		const adultBefore = toNum(before?.per_adult_rate_before_tax)
		if (isDifferent(adultBefore, adultNow)) {
			add(
				`${name} adult rate is updated ${adultBefore ?? 0} → ${adultNow ?? 0}`,
				"mealRates"
			)
		}

		if (!block.child_meal_rate_same_as_adult) {
			const childNow = toNum(mealRate?.per_child_rate_before_tax)
			const childBefore = toNum(before?.per_child_rate_before_tax)
			if (isDifferent(childBefore, childNow)) {
				add(
					`${name} child rate is updated ${childBefore ?? 0} → ${childNow ?? 0}`,
					"mealRates"
				)
			}
		}
	})

	// --- Room rates -------------------------------------------------------
	// One line per table rather than per cell, the same way the warnings and
	// errors collapse. A grid can hold dozens of edited cells, and each of them
	// already carries its own change dot in the table — at sheet level the
	// useful fact is which table to open.
	if (blockRateGridChanged(hotel, block, baseline)) {
		add("Changes found in room rates", "roomRates")
	}

	return changes
}

// Whether a comparison of these two blocks has to render the lower occupancy
// columns at all. The switch is per block, so one side can have them and the
// other not — and the side that still has them is the only record of the
// columns the grid used to carry.
export function lowerOccupancyAcrossBlocks(
	block: IRateBlockSchema | undefined,
	baseline: IRateBlockSchema | undefined | null
): boolean {
	return (
		Boolean(block?.is_lower_occupancy_rate_configured) ||
		Boolean(baseline?.is_lower_occupancy_rate_configured)
	)
}

// Whether any cell of a block's rate grid moved against its baseline. The same
// question `buildRateCellDiffs` answers per cell, asked once for the table —
// which is what lets a diff view decide whether a table is worth rendering at
// all without re-deriving the comparison or reading it out of a change string.
export function blockRateGridChanged(
	hotel: IHotel,
	block: IRateBlockSchema | undefined,
	baseline: IRateBlockSchema | undefined
): boolean {
	if (!block || !baseline) return false

	// Either side having priced lower occupancies is enough: turning the switch
	// off drops those columns and every rate under them, which is a change the
	// grid has to be wide enough to see.
	const configureLowerOccupancy = lowerOccupancyAcrossBlocks(block, baseline)

	// Built off the blocks' own pax types and rooms — the columns the rates were
	// priced in. Deriving them from the hotel leaves an edited sheet with a BASE
	// only grid, and a change in any extra pax column goes uncounted.
	const { lowerCols, roomCols, visibleRows } = buildBlockGridGeometry(
		hotel,
		block,
		configureLowerOccupancy,
		baseline
	)
	if (!visibleRows.length) return false

	// Both grids are built from the same geometry so their indices line up
	const currentGrid = buildGridRates(
		visibleRows,
		lowerCols,
		roomCols,
		configureLowerOccupancy,
		block.rates
	)
	const baselineGrid = buildGridRates(
		visibleRows,
		lowerCols,
		roomCols,
		configureLowerOccupancy,
		baseline.rates
	)

	return currentGrid.some((cell, idx) => {
		if (cell.enabled === 0 || cell.enabled === false) return false
		return isDifferent(
			toNum(baselineGrid[idx]?.rate_before_tax),
			toNum(cell.rate_before_tax)
		)
	})
}

export function computeBlockSummary(
	block: IRateBlockSchema | undefined,
	baseline: IRateBlockSchema | undefined,
	hotel: IHotel,
	hotelPolicy: TCancellationPolicy | null,
	hotelPaymentPolicy: TPaymentPolicy | null = null
): TBlockSummary {
	return {
		changes: computeBlockChanges(
			block,
			baseline,
			hotel,
			hotelPolicy,
			hotelPaymentPolicy
		),
		warnings: computeBlockWarnings(block, hotel),
		errors: computeBlockErrorMessages(block, hotel),
	}
}

// ---------------------------------------------------------------------------
// Whole sheet rollup
//
// Walks the full hotels x seasons cross product — including blocks the user has
// never opened, which never flush and would otherwise be missing from the
// counts entirely — and rolls each block summary up into the indicators the
// hotel sidebar and the season tabs render.
// ---------------------------------------------------------------------------

type TSummaryScope = "sheet" | "hotel" | "season" | "rate_block"
export type TSummaryKind = "change" | "warning" | "error"

export type TSummaryEntry = {
	scope: TSummaryScope
	kind: TSummaryKind
	// Who the entry belongs to: a hotel name, a season name, or "Hotel x Season"
	label: string
	message: string
	hotelId?: number
	seasonIdx?: number
	source?: TBlockSection
}

export type TSeasonIndicators = {
	changed: boolean
	hasPolicyError?: boolean
	warningSources: TIssueSource[]
	errorSources: TIssueSource[]
}

export type THotelIndicators = {
	changed: boolean
	hasWarnings: boolean
	hasErrors: boolean
}

export type TSeasonMeta = Pick<ISeasonSchema, "name" | "intervals"> & {
	_uid?: string
}

export type TSheetSummary = {
	changes: TSummaryEntry[]
	warnings: TSummaryEntry[]
	errors: TSummaryEntry[]
	byBlock: Map<string, TBlockSummary>
	// Both keyed by `rateKey(seasonIdx, hotelId)`, since the season tabs are
	// rendered per hotel
	bySeason: Map<string, TSeasonIndicators>
	byHotel: Map<number, THotelIndicators>
}

function emptySheetSummary(): TSheetSummary {
	return {
		changes: [],
		warnings: [],
		errors: [],
		byBlock: new Map(),
		bySeason: new Map(),
		byHotel: new Map(),
	}
}

export function formatIntervalRange(
	interval: ISeasonSchema["intervals"][number]
): string {
	const from = interval.from
		? formatDate(interval.from, "DD MMM, YYYY")
		: "No start date"
	const to = interval.to
		? formatDate(interval.to, "DD MMM, YYYY")
		: "No end date"
	return `${from} - ${to}`
}

// A season's date intervals in a canonical form, for deciding whether two
// seasons cover the same dates. Order carries no meaning on either the days or
// the intervals themselves, so both are sorted; the weekdays are part of it
// because the same dates sold on different days of the week are two different
// seasons.
export function flattenSeasonIntervals(
	intervals: TSeasonMeta["intervals"] | undefined
): string {
	return (intervals || [])
		.map((interval) => {
			const from = interval.from ? formatDate(interval.from, "YYYY-MM-DD") : ""
			const to = interval.to ? formatDate(interval.to, "YYYY-MM-DD") : ""
			const days = [...(interval.days || [])].sort().join(",")
			return `${from}|${to}|${days}`
		})
		.sort()
		.join("~")
}

// Season names are compared for identity, not displayed — case and stray
// whitespace are not what makes two seasons different.
export function normalizeSeasonName(name?: string | null): string {
	return (name || "").trim().replace(/\s+/g, " ").toLowerCase()
}

// The name of the first season already carrying this name, as the user typed
// it, so a message can quote the season they need to look for.
export function findConflictingSeasonName(
	name: string | null | undefined,
	otherSeasons: TSeasonMeta[]
): string | undefined {
	const normalized = normalizeSeasonName(name)
	if (!normalized) return undefined
	return otherSeasons.find(
		(season) => normalizeSeasonName(season.name) === normalized
	)?.name
}

// The weekdays that actually occur inside the closed range. Capped at 7 steps:
// beyond a week every weekday is present, so there is nothing left to find.
function weekdaysInRange(from: Date, to: Date): Set<TDay> {
	const days = new Set<TDay>()
	const end = startOf(to, "day")
	let cursor = startOf(from, "day")
	for (let i = 0; i < 7 && !isAfter(cursor, end, "day"); i++) {
		days.add(formatDate(cursor, "dddd") as TDay)
		cursor = addUnit(cursor, 1, "day")
	}
	return days
}

// Two intervals clash only when they sell the same night: their date ranges
// have to intersect *and* a weekday they both sell has to fall inside that
// intersection. Weekday-split seasons (Mon-Fri vs Sat-Sun over the same dates)
// are a legitimate setup, and so is an intersection too short to contain a
// shared weekday.
//
// A half-filled interval is nobody's conflict — the `required` validators own
// that case.
export function intervalsOverlap(
	a: TSeasonFormInterval,
	b: TSeasonFormInterval
) {
	if (!a.from || !a.to || !b.from || !b.to) return false
	const start = isAfter(a.from, b.from, "day") ? a.from : b.from
	const end = isAfter(a.to, b.to, "day") ? b.to : a.to
	if (isAfter(start, end, "day")) return false
	const inRange = weekdaysInRange(start, end)
	const bDays = new Set(b.days || [])
	return (a.days || []).some((day) => bDays.has(day) && inRange.has(day))
}

export type TSeasonIntervalConflict = {
	// Interval of the season being edited
	index: number
	// The conflicting interval, either elsewhere in the same season...
	withIndex?: number
	// ...or in another season of the sheet
	withSeasonName?: string
	range: string
}

// What one interval of the season being edited collides with — its own
// season's other intervals first, then the rest of the sheet. Only the first
// collision is reported: a single mistyped date can clash with everything, and
// a list of one error per season says no more than the first one does.
export function findIntervalConflict(
	intervals: TSeasonFormInterval[] | undefined,
	index: number,
	otherSeasons: TSeasonMeta[]
): TSeasonIntervalConflict | undefined {
	const current = intervals || []
	const interval = current[index]
	if (!interval) return undefined

	const withIndex = current.findIndex(
		(other, otherIdx) => otherIdx !== index && intervalsOverlap(interval, other)
	)
	if (withIndex >= 0) {
		return {
			index,
			withIndex,
			range: formatIntervalRange(current[withIndex]),
		}
	}

	for (const season of otherSeasons) {
		const clash = (season.intervals || []).find((other) =>
			intervalsOverlap(interval, other)
		)
		if (clash) {
			return {
				index,
				withSeasonName: season.name,
				range: formatIntervalRange(clash),
			}
		}
	}

	return undefined
}

// Every colliding interval of the season being edited. The form validates one
// interval at a time (each error has to land on its own From field); this is
// the whole-season view of the same thing.
export function findSeasonIntervalConflicts(
	intervals: TSeasonFormInterval[] | undefined,
	otherSeasons: TSeasonMeta[]
): TSeasonIntervalConflict[] {
	return (intervals || [])
		.map((_, index) => findIntervalConflict(intervals, index, otherSeasons))
		.filter((conflict): conflict is TSeasonIntervalConflict =>
			Boolean(conflict)
		)
}

export function describeIntervalConflict(
	conflict: TSeasonIntervalConflict
): string {
	return conflict.withIndex !== undefined
		? `Overlaps with interval ${conflict.withIndex + 1}`
		: `Overlaps with season: ${conflict.withSeasonName} (${conflict.range})`
}

// A yup test on `intervals[i].from` has to reach back out to the whole season
// to know what the interval it is validating collides with. `this.from` is the
// chain of object ancestors, innermost first, so the last entry is the form's
// own values.
function rootIntervalsOf(
	ancestors: readonly { value: unknown }[] | undefined
): TSeasonFormInterval[] | undefined {
	const root = ancestors?.[ancestors.length - 1]?.value as
		| { intervals?: TSeasonFormInterval[] }
		| undefined
	return root?.intervals
}

// "intervals[2].from" -> 2
function intervalIndexOf(path: string | undefined): number {
	const match = /\[(\d+)\]/.exec(path || "")
	return match ? Number(match[1]) : -1
}

// Body of the `intervals[i].from` overlap test, for any form whose values hold
// an `intervals` array — the season dialog here and the Hotels calendar's
// interval dialog, which has no other seasons to check against.
export function testNoOverlappingIntervals(
	context: Validator.TestContext,
	otherSeasons: TSeasonMeta[] = []
) {
	const conflict = findIntervalConflict(
		rootIntervalsOf(context.from),
		intervalIndexOf(context.path),
		otherSeasons
	)
	if (!conflict) return true
	return context.createError({ message: describeIntervalConflict(conflict) })
}

// The Add/Edit season dialog's schema. Built per set of sibling seasons rather
// than declared once, because uniqueness and overlap are only answerable
// against the rest of the sheet — `otherSeasons` excludes the season being
// edited, so reopening one and saving it unchanged is not a conflict with
// itself.
export function buildSeasonValidationSchema(otherSeasons: TSeasonMeta[]) {
	return Validator.object().shape({
		name: Validator.string()
			.min(4, "Name must be at least 4 characters long")
			.max(60, "Please use 60 or fewer characters")
			.required("Name is required")
			.trim()
			.test("unique-season-name", "", function (name) {
				const clash = findConflictingSeasonName(name, otherSeasons)
				if (!clash) return true
				return this.createError({
					message: `"${clash}" season already exists in this rate sheet`,
				})
			}),
		intervals: Validator.array()
			.of(
				Validator.object().shape({
					// Dates rather than strings: the pickers hold `Date`s, and casting
					// them to strings sends the overlap check below through moment's
					// unreliable date-string fallback
					from: Validator.date()
						.nullable()
						.typeError("Please select a valid date")
						.required("From date is required")
						// Anchored on the interval's own From field rather than raised as
						// a batch from an array-level test: yup drops a thrown batch of
						// errors as soon as any sibling item fails its own validation
						.test("no-overlapping-intervals", "", function () {
							return testNoOverlappingIntervals(this, otherSeasons)
						}),
					to: Validator.date()
						.nullable()
						.typeError("Please select a valid date")
						.required("To date is required")
						.test(
							"is-after-from",
							"To date must be after or equal to From date",
							function (to) {
								const { from } = this.parent
								if (!from || !to) return true
								return !isAfter(from, to)
							}
						),
					days: Validator.array()
						.required("At least one day needs to be selected")
						.min(1, "At least one day needs to be selected"),
				})
			)
			.required("Intervals is required"),
	})
}

export type TSeasonPairing<T extends TSeasonMeta> = {
	// Index aligned with the seasons passed in. An undefined `baseline` is a
	// season the other side never had.
	matches: Array<{ baseline: T | undefined; baselineIdx: number }>
	// Baseline seasons nothing paired with — the ones that went away
	unmatched: Array<{ season: T; seasonIdx: number }>
}

// Pairs the seasons of two versions of a sheet, one to one.
//
// Nothing in the payload identifies a season across versions: saving copies the
// whole tree, so every version holds fresh season rows with fresh ids and the
// uid (`season_{id}`) names a row rather than a season. What is left is what
// the season is made of, so the passes below go from the strongest evidence to
// the weakest:
//
//   uid       — only ever matches within one editing session, where both sides
//               are the same objects. Authoritative there, and the only pass
//               that survives a season being renamed and re-dated at once.
//   name      — what carries the pairing between two saved versions.
//   intervals — a renamed season, still covering exactly the same dates.
//
// Each pass claims from one shared pool. Without that a baseline can be matched
// twice — which is what made a newly added season report as a rename of
// whichever season happened to sit at its index.
export function pairSeasons<T extends TSeasonMeta>(
	seasons: TSeasonMeta[],
	baselineSeasons: T[] | undefined
): TSeasonPairing<T> {
	const baselines = baselineSeasons || []
	const matches: TSeasonPairing<T>["matches"] = seasons.map(() => ({
		baseline: undefined,
		baselineIdx: -1,
	}))
	const claimed = new Set<number>()

	function claimBy(keyOf: (season: TSeasonMeta) => string | undefined) {
		seasons.forEach((season, seasonIdx) => {
			if (matches[seasonIdx].baseline) return
			const key = keyOf(season)
			if (!key) return
			const baselineIdx = baselines.findIndex(
				(candidate, idx) => !claimed.has(idx) && keyOf(candidate) === key
			)
			if (baselineIdx < 0) return
			claimed.add(baselineIdx)
			matches[seasonIdx] = { baseline: baselines[baselineIdx], baselineIdx }
		})
	}

	claimBy((season) => season._uid)
	claimBy((season) => season.name || undefined)
	// A season with no intervals flattens to nothing, and two seasons that both
	// describe nothing are not evidence of being the same season
	claimBy((season) => flattenSeasonIntervals(season.intervals) || undefined)

	return {
		matches,
		unmatched: baselines
			.map((season, seasonIdx) => ({ season, seasonIdx }))
			.filter(({ seasonIdx }) => !claimed.has(seasonIdx)),
	}
}

// Name and interval changes for one season. Rate block changes are attributed
// to the block, not here.
export function computeSeasonChanges(
	season: TSeasonMeta,
	baseline: TSeasonMeta | undefined
): string[] {
	const changes: string[] = []
	if (!baseline) {
		changes.push(`Season "${season.name}" added`)
		return changes
	}

	if (season.name !== baseline.name) {
		changes.push(`Season renamed: ${baseline.name} → ${season.name}`)
	}

	const current = season.intervals || []
	const before = baseline.intervals || []
	const shared = Math.min(current.length, before.length)

	for (let i = 0; i < shared; i++) {
		const now = current[i]
		const was = before[i]
		if (
			now.from &&
			now.to &&
			was.from &&
			was.to &&
			(!isSame(now.from, was.from, "day") || !isSame(now.to, was.to, "day"))
		) {
			changes.push(
				`Interval updated: ${formatIntervalRange(was)} → ${formatIntervalRange(now)}`
			)
		}
		const formatIntervalDays = (days: string[] | undefined) =>
			[...(days || [])].map((day) => day.slice(0, 3)).join(", ")

		const daysNow = formatIntervalDays(now.days)
		const daysWas = formatIntervalDays(was.days)
		if (daysNow !== daysWas) {
			changes.push(
				`Interval days updated for ${formatIntervalRange(now)}: ${
					daysWas || "none"
				} → ${daysNow || "none"}`
			)
		}
	}

	for (let i = shared; i < current.length; i++) {
		changes.push(`Interval added: ${formatIntervalRange(current[i])}`)
	}
	for (let i = shared; i < before.length; i++) {
		changes.push(`Interval removed: ${formatIntervalRange(before[i])}`)
	}

	return changes
}

// The policy listings, as the wizard holds them.
export type TPolicyHeads = {
	cancellation?: TCancellationPolicyDetail[]
	payment?: TPaymentPolicyDetail[]
}

// The policies each hotel of a sheet sells under, keyed by hotel id. Null is
// "no policy chosen" for that hotel.
export type THotelPolicyMaps = {
	hotelPolicies: Record<number, TCancellationPolicy | null>
	hotelPaymentPolicies: Record<number, TPaymentPolicy | null>
}

// What is wrong with the policies a sheet sells under: a hotel with no policy
// picked, or one pinned to a version its head has moved past. Both block the
// sheet, and step 1 renders them before the summary exists — hence a standalone
// function rather than something folded into `computeSheetSummary`.
export function computePolicyIssues({
	hotels,
	hotelPolicies,
	hotelPaymentPolicies,
	policyHeads,
}: Partial<THotelPolicyMaps> & {
	hotels: IHotel[]
	policyHeads?: TPolicyHeads
}): TSummaryEntry[] {
	const issues: TSummaryEntry[] = []
	hotels.forEach((hotel) => {
		const hotelId = Number(hotel.id)
		const kinds = [
			{
				label: "Cancellation policy",
				policy: (hotelPolicies?.[hotelId] || null) as TAnyPolicy | null,
				heads: policyHeads?.cancellation as TAnyPolicyDetail[] | undefined,
			},
			{
				label: "Payment policy",
				policy: (hotelPaymentPolicies?.[hotelId] || null) as TAnyPolicy | null,
				heads: policyHeads?.payment as TAnyPolicyDetail[] | undefined,
			},
		]
		kinds.forEach(({ label, policy, heads }) => {
			if (!policy) {
				issues.push({
					scope: "hotel",
					kind: "error",
					label: hotel.name,
					message: `${label} not selected`,
					hotelId,
				})
				return
			}
			if (isPolicyStale(policy, heads)) {
				const latest = latestPolicyFor(policy, heads)
				issues.push({
					scope: "hotel",
					kind: "error",
					label: hotel.name,
					message: `${label} is on v${policy.version}, latest is v${latest?.version}`,
					hotelId,
				})
			}
		})
	})
	return issues
}

export function computeSheetSummary({
	name,
	originalName,
	hotels,
	hotelDetailsById,
	seasons,
	hotelPolicies,
	hotelPaymentPolicies,
	policyHeads,
	ratesRef,
	originalRatesRef,
	originalSeasons,
	originalHotels,
	originalHotelPolicies,
	originalHotelPaymentPolicies,
	isEdit,
}: {
	name?: string
	originalName?: string
	hotels: IHotel[]
	// The hotels as returned by `/hotels/{id}`, keyed by id. The hotels picked in
	// step 1 come from the list endpoint, which carries no OTA room types or meal
	// plans — and the rate grid's geometry is derived entirely from those. Without
	// the detailed record a block summary can still report missing rates, but
	// every warning that needs to know the shape of the grid silently comes back
	// empty. See `computeBlockWarnings`.
	hotelDetailsById?: Map<number, IHotel>
	seasons: TSeasonMeta[]
	hotelPolicies: THotelPolicyMaps["hotelPolicies"]
	hotelPaymentPolicies?: THotelPolicyMaps["hotelPaymentPolicies"]
	// The policy listings, needed to tell a pinned version from its head's
	// latest. Without them staleness cannot be judged and is not reported.
	policyHeads?: TPolicyHeads
	ratesRef: Map<string, IRateBlockSchema>
	originalRatesRef?: Map<string, IRateBlockSchema>
	originalSeasons?: TSeasonMeta[]
	originalHotels?: IHotel[]
	originalHotelPolicies?: THotelPolicyMaps["hotelPolicies"]
	originalHotelPaymentPolicies?: THotelPolicyMaps["hotelPaymentPolicies"]
	isEdit: boolean
}): TSheetSummary {
	const summary = emptySheetSummary()
	const push = (entry: TSummaryEntry) => {
		if (entry.kind === "change") summary.changes.push(entry)
		else if (entry.kind === "warning") summary.warnings.push(entry)
		else summary.errors.push(entry)
	}

	// --- Rate sheet scope -------------------------------------------------
	if (isEdit && originalName && name && name.trim() !== originalName.trim()) {
		push({
			scope: "sheet",
			kind: "change",
			label: "Rate Sheet",
			message: `Rate sheet renamed: ${originalName.trim()} → ${name.trim()}`,
		})
	}

	// --- Hotel scope ------------------------------------------------------
	// A sheet cannot be sold under a policy that is not set, or under a version
	// its head has moved past. Only reported once the listings are in hand:
	// without them a version cannot be told from its head's latest, and step 1
	// has already blocked on anything missing.
	if (policyHeads) {
		computePolicyIssues({
			hotels,
			hotelPolicies,
			hotelPaymentPolicies,
			policyHeads,
		}).forEach(push)
	}

	if (isEdit) {
		const originalById = new Map(
			(originalHotels || []).map((h) => [Number(h.id), h])
		)
		hotels.forEach((hotel) => {
			const hotelId = Number(hotel.id)
			if (originalHotels && !originalById.has(hotelId)) {
				push({
					scope: "hotel",
					kind: "change",
					label: hotel.name,
					message: "Added to rate sheet",
					hotelId,
				})
				return
			}
			const cancellationChange = describePolicyChange(
				"Cancellation policy",
				originalHotelPolicies?.[hotelId] || null,
				hotelPolicies?.[hotelId] || null
			)
			if (cancellationChange) {
				push({
					scope: "hotel",
					kind: "change",
					label: hotel.name,
					message: cancellationChange,
					hotelId,
				})
			}
			const paymentChange = describePolicyChange(
				"Payment policy",
				originalHotelPaymentPolicies?.[hotelId] || null,
				hotelPaymentPolicies?.[hotelId] || null
			)
			if (paymentChange) {
				push({
					scope: "hotel",
					kind: "change",
					label: hotel.name,
					message: paymentChange,
					hotelId,
				})
			}
		})

		const currentIds = new Set(hotels.map((h) => Number(h.id)))
		;(originalHotels || []).forEach((hotel) => {
			if (!currentIds.has(Number(hotel.id))) {
				push({
					scope: "hotel",
					kind: "change",
					label: hotel.name,
					message: "Removed from rate sheet",
				})
			}
		})
	}

	// --- Season scope -----------------------------------------------------
	// Paired once for the whole sheet, and read again by the rate block scope
	// below, so a season cannot be counted as renamed in one place and as added
	// in the other.
	const seasonPairing = pairSeasons(seasons, originalSeasons)
	const seasonChangesByKey = new Map<string | number, string[]>()
	if (isEdit) {
		seasons.forEach((season, seasonIdx) => {
			const baseline = seasonPairing.matches[seasonIdx]?.baseline

			const changes = computeSeasonChanges(season, baseline)
			const sKey = season._uid || seasonIdx
			if (changes.length > 0) {
				seasonChangesByKey.set(sKey, changes)
				seasonChangesByKey.set(seasonIdx, changes)
				changes.forEach((message) =>
					push({
						scope: "season",
						kind: "change",
						label: season.name,
						message,
						seasonIdx,
					})
				)
			}
		})
		seasonPairing.unmatched.forEach(({ season, seasonIdx }) => {
			push({
				scope: "season",
				kind: "change",
				label: season.name || `Season ${seasonIdx + 1}`,
				message: "Season removed",
			})
		})
	}

	// --- Rate block scope -------------------------------------------------
	hotels.forEach((hotel) => {
		const hotelId = Number(hotel.id)
		// Falls back to the step 1 record for hotels whose detail has not been
		// fetched yet — those still report missing rates, just not grid warnings.
		// A list record carries no room types and a sheet loaded for editing only
		// part of the configuration, so a grid built from either is not the one
		// the user will see, and any warning measured against it is noise.
		const hotelDetail = hotelDetailsById?.get(hotelId) || hotel
		const canMeasureWarnings =
			!hotelDetailsById || hotelDetailsById.has(hotelId)
		const hotelPolicy = hotelPolicies?.[hotelId] || null
		const hotelPaymentPolicy = hotelPaymentPolicies?.[hotelId] || null
		const hotelIndicators: THotelIndicators = {
			// A hotel level change already recorded above lights the sidebar dot too
			changed: summary.changes.some(
				(entry) => entry.scope === "hotel" && entry.hotelId === hotelId
			),
			hasWarnings: false,
			// As does a missing or out of date policy
			hasErrors: summary.errors.some(
				(entry) => entry.scope === "hotel" && entry.hotelId === hotelId
			),
		}

		seasons.forEach((season, seasonIdx) => {
			const sKey = season._uid || seasonIdx
			const key = rateKey(sKey, hotelId)
			const block =
				ratesRef.get(key) || ratesRef.get(rateKey(seasonIdx, hotelId))
			// Addressed through the pairing rather than through this season's own
			// key: the two versions key their blocks by their own uids, and reading
			// the baseline by index instead would hand back whichever block happened
			// to sit at the same position.
			const paired = isEdit ? seasonPairing.matches[seasonIdx] : undefined
			const baseline = paired?.baseline
				? originalRatesRef?.get(
						rateKey(paired.baseline._uid || paired.baselineIdx, hotelId)
					)
				: undefined

			const computedBlockSummary = computeBlockSummary(
				block,
				baseline,
				hotelDetail,
				hotelPolicy,
				hotelPaymentPolicy
			)
			// A season added in this edit has nothing to be compared against; the
			// season scope already reports it as added, so its blocks carry no
			// changes of their own. Warnings and errors still apply.
			const blockSummary: TBlockSummary = {
				...computedBlockSummary,
				changes:
					isEdit && !paired?.baseline ? [] : computedBlockSummary.changes,
				warnings: canMeasureWarnings
					? computedBlockSummary.warnings
					: { roomRates: [], mealRates: [] },
			}
			summary.byBlock.set(key, blockSummary)
			summary.byBlock.set(rateKey(seasonIdx, hotelId), blockSummary)

			const label = `${hotel.name} × ${season.name}`
			blockSummary.changes.forEach(({ message, section }) =>
				push({
					scope: "rate_block",
					kind: "change",
					label,
					message,
					hotelId,
					seasonIdx,
					source: section,
				})
			)
			// One line per table rather than per cell. A 4x4 grid can hold dozens of
			// issues, and each of them is already reported in full by that table's own
			// summary — at sheet level the useful fact is which table to open.
			;(["mealRates", "roomRates"] as const).forEach((source) => {
				const table = source === "mealRates" ? "meal rates" : "room rates"
				if (blockSummary.warnings[source].length > 0) {
					push({
						scope: "rate_block",
						kind: "warning",
						label,
						message: `Warnings found in ${table} table`,
						hotelId,
						seasonIdx,
						source,
					})
				}
				if (blockSummary.errors[source].length > 0) {
					push({
						scope: "rate_block",
						kind: "error",
						label,
						message: `Errors found in ${table} table`,
						hotelId,
						seasonIdx,
						source,
					})
				}
			})

			// A block that overrode a policy holds its own pinned version, which
			// step 1's check on the hotel's policies never sees. Non-manual blocks
			// mirror the hotel, so the hotel level error already covers them.
			let hasPolicyError = false
			if (block && policyHeads) {
				;(
					[
						{
							policyLabel: "Cancellation policy",
							isManual: block.cancellation_policy_is_manual,
							policy: block.cancellation_policy as TAnyPolicy | null,
							heads: policyHeads.cancellation as TAnyPolicyDetail[] | undefined,
						},
						{
							policyLabel: "Payment policy",
							isManual: block.payment_policy_is_manual,
							policy: block.payment_policy as TAnyPolicy | null,
							heads: policyHeads.payment as TAnyPolicyDetail[] | undefined,
						},
					] as const
				).forEach(({ policyLabel, isManual, policy, heads }) => {
					if (!isManual || !policy || !isPolicyStale(policy, heads)) return
					const latest = latestPolicyFor(policy, heads)
					hasPolicyError = true
					push({
						scope: "rate_block",
						kind: "error",
						label,
						message: `${policyLabel} is on v${policy.version}, latest is v${latest?.version}`,
						hotelId,
						seasonIdx,
					})
				})
			}

			const warningSources = (["mealRates", "roomRates"] as const).filter(
				(source) => blockSummary.warnings[source].length > 0
			)
			const errorSources = (["mealRates", "roomRates"] as const).filter(
				(source) => blockSummary.errors[source].length > 0
			)

			const seasonIndicators: TSeasonIndicators = {
				hasPolicyError,
				changed:
					blockSummary.changes.length > 0 ||
					(seasonChangesByKey.get(sKey)?.length || 0) > 0 ||
					(seasonChangesByKey.get(seasonIdx)?.length || 0) > 0,
				warningSources: [...warningSources],
				errorSources: [...errorSources],
			}

			summary.bySeason.set(key, seasonIndicators)
			summary.bySeason.set(rateKey(seasonIdx, hotelId), seasonIndicators)

			if (blockSummary.changes.length > 0) hotelIndicators.changed = true
			if (warningSources.length > 0) hotelIndicators.hasWarnings = true
			if (errorSources.length > 0 || hasPolicyError) {
				hotelIndicators.hasErrors = true
			}
		})

		// A season rename or interval edit shows on every hotel's season tabs, so
		// it lights the hotel dot too
		if (seasonChangesByKey.size > 0) hotelIndicators.changed = true

		summary.byHotel.set(hotelId, hotelIndicators)
	})

	return summary
}

// The part of the block a group of summary entries points at, which is what the
// status bar's "View" scrolls to. A group can mix entries with and without one (a
// stale policy error has none), so the first that names a section wins.
export function pickFocusSource(
	entries: TSummaryEntry[]
): TBlockSection | undefined {
	return entries.find((entry) => entry.source)?.source
}

// "meal rates and room rates" — used by the season tab tooltips
export function describeIssueSources(
	sources: (TIssueSource | "policies")[]
): string {
	const names = sources.map((source) =>
		source === "mealRates"
			? "meal rates"
			: source === "roomRates"
				? "room rates"
				: "policies"
	)
	if (names.length === 0) return ""
	if (names.length === 1) return names[0]
	return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
}

// ---------------------------------------------------------------------------
// Two saved versions, side by side
//
// What the version history dialog renders. Kept here, away from the component,
// because pairing seasons across versions and deciding what is worth showing is
// exactly the kind of thing that is easy to get subtly wrong and easy to test.
// ---------------------------------------------------------------------------

// What happened to a hotel or one of its seasons between two versions.
// `updated` is the only one whose children still have to be examined — the
// other two paint everything below them.
export type TVersionDiffStatus = "added" | "removed" | "updated"

// One hotel x season of the comparison, with both sides already resolved.
export type TSeasonDiffEntry = {
	// Stable across the two versions, for a render key
	key: string
	// Index into the version that still has this season — the current seasons for
	// an "added"/"updated" entry, but the *baseline* seasons for a "removed" one.
	// So it only lines up with the sheet summary's per-block keys, which are
	// always current-side, while the status is "updated".
	seasonIdx: number
	name: string
	status: TVersionDiffStatus
	block: IRateBlockSchema | undefined
	baselineBlock: IRateBlockSchema | undefined
	// Renames and interval edits, which belong to the season rather than to any
	// one hotel's block
	seasonChanges: string[]
}

// Every season of both versions for one hotel, newest version's order first,
// then the seasons only the older version had.
//
// Seasons are paired by `pairSeasons`, the same way `computeSheetSummary` pairs
// them, so a rename cannot read as an add plus a remove here while the summary
// calls it a rename.
export function buildSeasonDiffEntries({
	hotelId,
	hotelStatus,
	seasons,
	baselineSeasons,
	ratesRef,
	baselineRatesRef,
}: {
	hotelId: number
	hotelStatus: TVersionDiffStatus
	seasons: TSeasonMeta[]
	baselineSeasons: TSeasonMeta[]
	ratesRef: Map<string, IRateBlockSchema>
	baselineRatesRef: Map<string, IRateBlockSchema>
}): TSeasonDiffEntry[] {
	const pairing = pairSeasons(seasons, baselineSeasons)

	const entries: TSeasonDiffEntry[] = seasons.map((season, seasonIdx) => {
		const { baseline, baselineIdx } = pairing.matches[seasonIdx]

		// `buildSheetRatesRef` writes every block under both its season's uid and
		// its index, so the key each side was built with always hits.
		const block = ratesRef.get(rateKey(season._uid || seasonIdx, hotelId))
		const baselineBlock = baseline
			? baselineRatesRef.get(rateKey(baseline._uid || baselineIdx, hotelId))
			: undefined

		// A hotel added or removed wholesale paints every season under it. Below
		// that it is the block that decides: a hotel can stop being priced in a
		// season the sheet still has, which is a removal even though both the
		// hotel and the season survived.
		const status: TVersionDiffStatus =
			hotelStatus !== "updated"
				? hotelStatus
				: baselineBlock && !block
					? "removed"
					: baselineBlock
						? "updated"
						: "added"

		return {
			key: `${season._uid || seasonIdx}`,
			seasonIdx,
			name: season.name,
			status,
			block,
			baselineBlock,
			seasonChanges: baseline ? computeSeasonChanges(season, baseline) : [],
		}
	})

	pairing.unmatched.forEach(({ season, seasonIdx }) => {
		entries.push({
			key: `removed_${season._uid || seasonIdx}`,
			seasonIdx,
			name: season.name,
			status: "removed",
			block: undefined,
			baselineBlock: baselineRatesRef.get(
				rateKey(season._uid || seasonIdx, hotelId)
			),
			seasonChanges: [],
		})
	})

	return entries
}

// Whether either policy a season sells under moved. An entry that is not an
// update has only one side, which is a change by definition.
export function seasonDiffPolicyChanged(entry: TSeasonDiffEntry): boolean {
	if (entry.status !== "updated") return true
	return (
		(entry.block?.cancellation_policy?.id ?? null) !==
			(entry.baselineBlock?.cancellation_policy?.id ?? null) ||
		(entry.block?.payment_policy?.id ?? null) !==
			(entry.baselineBlock?.payment_policy?.id ?? null)
	)
}

// Whether the rate grid is worth drawing for this entry.
export function seasonDiffRatesChanged(
	entry: TSeasonDiffEntry,
	hotel: IHotel
): boolean {
	if (entry.status !== "updated") return true
	return blockRateGridChanged(hotel, entry.block, entry.baselineBlock)
}

// Whether a hotel x season is worth rendering at all: the season itself moved,
// its rate grid moved, its policy moved, or the block reported some other
// change. `blockChanges` is the block's own entry in the sheet summary, which
// covers rounding, pricing type, meal plans and meal rates.
export function seasonDiffHasChanges(
	entry: TSeasonDiffEntry,
	hotel: IHotel,
	blockChanges: string[] | undefined
): boolean {
	// Never priced on either side — nothing to say about it even if the hotel
	// itself came or went
	if (!entry.block && !entry.baselineBlock) return false
	if (entry.status !== "updated") return true
	if (entry.seasonChanges.length > 0) return true
	if (seasonDiffRatesChanged(entry, hotel)) return true
	if (seasonDiffPolicyChanged(entry)) return true
	return Boolean(blockChanges?.length)
}

// The query string for a page inside the shared (token) view. Every page there
// is reached through the recipient's token, so it has to ride along on each
// link; it is set last so no other query value can replace it.
export function shareTokenSearch(
	token: string,
	query?: Record<string, string | number | null | undefined>
): string {
	const search = new URLSearchParams()
	Object.entries(query || {}).forEach(([key, value]) => {
		if (value === null || value === undefined || value === "") return
		search.set(key, String(value))
	})
	search.set("guest_token", token)
	return `?${search.toString()}`
}

export function withShareToken(
	path: string,
	token: string,
	query?: Record<string, string | number | null | undefined>
): string {
	return `${path}${shareTokenSearch(token, query)}`
}

// The months a sheet's seasons span, from its earliest interval's start to its
// latest interval's end — seasons are not kept in date order. The year is said
// once when both ends share it: "Jan - Dec 2026", "Jan 2026 - Jul 2027".
export function formatSeasonsMonthRange(
	seasons: Array<Pick<TRateSheetSeason, "intervals">>
): string | null {
	const intervals = seasons.flatMap((season) => season.intervals)
	if (!intervals.length) return null
	let from = intervals[0].from
	let to = intervals[0].to
	intervals.forEach((interval) => {
		if (isBefore(interval.from, from)) from = interval.from
		if (isAfter(interval.to, to)) to = interval.to
	})
	if (isSame(from, to, "month")) return formatDate(from, "MMM YYYY")
	if (isSame(from, to, "year")) {
		return `${formatDate(from, "MMM")} - ${formatDate(to, "MMM YYYY")}`
	}
	return `${formatDate(from, "MMM YYYY")} - ${formatDate(to, "MMM YYYY")}`
}
